<template>
  <div class="vendor_enter_bill-main glass-tab-page">
    <div class="vendor_enter_bill-header-bar">
      <BrucdcumHeader :breadcrumb="['Vendor', 'Enter Bill']" />
    </div>

    <CreateBillProgressor
      progress-title="Enter bill progress"
      progress-subtitle="Saving the bill and posting the journal entry run in order. When every step is finished, click OK to complete."
      :show="enterBillProgressorShow"
      :steps="enterBillProgressorSteps"
      :ok-enabled="enterBillProgressorOkEnabled"
      :foot-message="enterBillProgressorFootMessage"
      :foot-tone="enterBillProgressorFootTone"
      @ok="onEnterBillProgressorOk"
    />

    <div
      v-if="journalAutoCreateActive"
      class="vendor-enter-bill-journal-auto-create-root"
      aria-hidden="true"
    >
      <BillJournalEntry
        :bill-data="billDataForJournal"
        :is-modal="true"
        :auto-populate="true"
        :auto-create="true"
        @saved="handleJournalEntrySaved"
        @auto-create-skipped="handleJournalEntryAutoSkipped"
        @auto-create-failed="handleJournalEntryAutoFailed"
        @cancelled="closeJournalEntryModal"
      />
    </div>

    <form class="vendor_enter_bill-form" @submit.prevent>
      <div class="glass-tab-view glass-tab-view--flush vendor_enter_bill-glass-shell">
        <div class="glass-tab-view__content vendor_enter_bill-tab-content">
          <div class="glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows">
            <div class="vendor_enter_bill-invoice-meta-row">
              <div class="vendor_enter_bill-form-group vendor_enter_bill-form-group--label-date vendor_enter_bill-meta-field">
                <LabelDateField
                  input-id="enter-bill-date"
                  label="Bill Date"
                  v-model="billDate"
                  placeholder="MM/DD/YYYY"
                  flex
                />
              </div>
              <div class="vendor_enter_bill-form-group vendor_enter_bill-form-group--label-input vendor_enter_bill-meta-field">
                <LabelInputField
                  input-id="enter-bill-no"
                  label="Bill No"
                  v-model="invNo"
                  placeholder="Bill No"
                  flex
                />
              </div>
            </div>

            <div class="vendor_enter_bill-form-row vendor_enter_bill-form-row--columns glass-tab-panel__row">
              <div class="vendor_enter_bill-form-col">
                <div class="vendor_enter_bill-form-group vendor_enter_bill-form-group--label-dropdown vendor_enter_bill-required">
                  <LabelDropdown
                    label="Vendor Name"
                    select-id="enter-bill-vendor"
                    v-model="vendorId"
                    flex
                    wide
                    required
                    searchable
                    :options="vendorDropdownOptions"
                  />
                </div>
                <div v-if="showPrefillMessage" class="vendor-enter-bill-prefill-info">
                  <small>Pre-selected: {{ $route.query.vendorName }}</small>
                </div>
                <div class="vendor_enter_bill-form-group vendor_enter_bill-form-group--label-dropdown">
                  <LabelDropdown
                    label="Contact Name"
                    select-id="enter-bill-contact"
                    v-model="contactName"
                    flex
                    wide
                    searchable
                    :options="contactDropdownOptions"
                  />
                </div>
              </div>
              <div class="vendor_enter_bill-form-col">
                <div class="vendor_enter_bill-form-group vendor_enter_bill-form-group--label-input">
                  <LabelInputField
                    input-id="enter-bill-phone"
                    label="Phone"
                    v-model="phone"
                    placeholder="Phone"
                    flex
                  />
                </div>
                <div class="vendor_enter_bill-form-group vendor_enter_bill-form-group--label-dropdown">
                  <LabelDropdown
                    label="Payment Terms"
                    select-id="enter-bill-payment-term"
                    v-model="paymentTerm"
                    flex
                    wide
                    searchable
                    :options="paymentTermDropdownOptions"
                    @change="handlePaymentTermChange"
                  />
                </div>
              </div>
              <div class="vendor_enter_bill-form-col">
                <div class="vendor_enter_bill-form-group vendor_enter_bill-form-group--label-date">
                  <LabelDateField
                    input-id="enter-bill-due-date"
                    label="Due Date"
                    v-model="dueDate"
                    placeholder="MM/DD/YYYY"
                    flex
                  />
                </div>
              </div>
            </div>
            <div v-if="!vendorId" class="vendor_enter_bill-glass-empty vendor-enter-bill-no-vendor-message">
              <div class="vendor-enter-bill-no-vendor-icon" aria-hidden="true">
                <i class="bi bi-shop"></i>
              </div>
              <h3>Please Select a Vendor</h3>
              <p>Kindly select a vendor from the dropdown above to proceed with bill details and itemize tabs.</p>
            </div>

            <div v-else class="vendor_enter_bill-itemize-wrap">
              <div class="vendor_enter_bill-tabs">
                <button
                  v-if="isConsultantIndustry"
                  type="button"
                  class="vendor_enter_bill-tab"
                  :class="{ 'vendor_enter_bill-tab-active': activeTab === 'consultant' }"
                  @click="activeTab = 'consultant'"
                >
                  <i class="bi bi-person-workspace" aria-hidden="true"></i>
                  Itemize by Consultant Hours
                </button>
                <button
                  type="button"
                  class="vendor_enter_bill-tab"
                  :class="{ 'vendor_enter_bill-tab-active': activeTab === 'account' }"
                  @click="activeTab = 'account'"
                >
                  <i class="bi bi-journal-text" aria-hidden="true"></i>
                  Itemize by Account
                </button>
                <button
                  type="button"
                  class="vendor_enter_bill-tab"
                  :class="{ 'vendor_enter_bill-tab-active': activeTab === 'product' }"
                  @click="activeTab = 'product'"
                >
                  <i class="bi bi-box-seam" aria-hidden="true"></i>
                  Itemize by Product/Service
                </button>
              </div>

              <div v-show="activeTab === 'account'" class="vendor_enter_bill-itemize-section">
          <div class="vendor_enter_bill-glass-hint vendor_enter_bill-account-tab-hint">
            <i class="bi bi-info-circle" aria-hidden="true"></i>
            <div class="vendor_enter_bill-account-hint-body">
              <p class="vendor_enter_bill-info-text">
                Each product/service item<span v-if="isConsultantIndustry"> and consultant timesheet</span> appears as an individual account entry with its own amount.
              </p>
              <p class="vendor_enter_bill-info-text">
                <strong>Available Account Types:</strong> Expense, Other Expense, Cost of Goods Sold, Current Liability, Payroll Liability
              </p>
              <p class="vendor_enter_bill-info-text">
                <strong>Purpose:</strong> Categorize vendor bills into specific accounting accounts for proper financial reporting and tracking.
              </p>
            </div>
          </div>

          <!-- Show table only when there are account rows with amounts > 0 -->
          <div v-if="visibleAccountRows.length > 0" class="consultant-glass-table-section vendor_enter_bill-table-section">
            <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
              <div class="consultant-glass-table-scroll-x">
            <table class="consultant-glass-table vendor_enter_bill-itemize-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Description</th>
                  <th>Amount(USD)</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in visibleAccountRows" :key="'account-' + idx" class="consultant-glass-table-row">
                  <td>
                    <select v-model="row.accountId" class="vendor_enter_bill-itemize-table-select" disabled>
                      <option value="">Select</option>
                      <option v-for="account in (allCompanyAccounts.length ? allCompanyAccounts : accountList)" :key="account.id" :value="account.id">
                        {{ account.accountNumber ? account.accountNumber + ' - ' : '' }}{{ account.accountName }}{{ account.accountType ? ' - ' + account.accountType : '' }}
                      </option>
                    </select>
                  </td>
                  <td>
                    <div class="vendor_enter_bill-item-description vendor_enter_bill-glass-cell-desc">
                      <div class="vendor_enter_bill-item-name">
                        <i v-if="row.itemType === 'consultant'" class="bi bi-person-workspace vendor_enter_bill-item-type-icon" aria-hidden="true"></i>
                        <i v-else-if="row.itemType === 'product'" class="bi bi-box-seam vendor_enter_bill-item-type-icon" aria-hidden="true"></i>
                        <i v-else-if="row.itemType === 'service'" class="bi bi-tools vendor_enter_bill-item-type-icon" aria-hidden="true"></i>
                        <i v-else-if="row.itemType === 'Inventory'" class="bi bi-boxes vendor_enter_bill-item-type-icon" aria-hidden="true"></i>
                        {{ row.itemName }}
                      </div>
                      <div class="vendor_enter_bill-item-details">
                        <span v-if="row.itemType === 'consultant'">
                          Timesheet: {{ row.hours }}h × ${{ row.hourlyRate }} = ${{ row.amount.toFixed(2) }}
                        </span>
                        <span v-else-if="row.itemType === 'product' || row.itemType === 'service' || row.itemType === 'Inventory'">
                          {{ row.itemType }}: {{ row.quantity }} × ${{ row.unitPrice }} = ${{ row.amount.toFixed(2) }}
                        </span>
                        <span class="vendor_enter_bill-source-info">({{ row.source }})</span>
                      </div>
                    </div>
                  </td>
                  <td><input type="number" v-model="row.amount" class="vendor_enter_bill-itemize-table-input" readonly /></td>
                  <td>{{ row.amount ? row.amount.toFixed(2) : '' }}</td>
                </tr>
                <tr>
                  <td colspan="4" class="vendor_enter_bill-table-total-row">Total (USD): <span class="vendor_enter_bill-total-value">$ {{ accountTotal.toFixed(2) }}</span></td>
                </tr>
              </tbody>
            </table>
              </div>
            </div>
          </div>
          
          <!-- Show message when no account records with amounts are available -->
          <div v-if="visibleAccountRows.length === 0" class="vendor_enter_bill-glass-empty vendor-enter-bill-no-vendor-message">
            <div class="vendor-enter-bill-no-vendor-icon" aria-hidden="true">
              <i class="bi bi-journal-text"></i>
            </div>
            <h3>No Expense Account Records</h3>
            <p v-if="isConsultantIndustry">This vendor has no available vendor product/service items or consultant records with billable amounts. Items may have already been added to other bills.</p>
            <p v-else>This vendor has no available vendor product/service items with billable amounts. Items may have already been added to other bills.</p>
          </div>
              </div>

              <div v-if="isConsultantIndustry" v-show="activeTab === 'consultant'" class="vendor_enter_bill-consultant-section">
        <div class="vendor_enter_bill-info-stack">
            <div class="vendor_enter_bill-glass-hint vendor-enter-bill-auto-info">
              <i class="bi bi-info-circle" aria-hidden="true"></i>
              <small>Available consultant timesheets are automatically added when you select a vendor.</small>
            </div>
            <div
              v-if="consultantsLoading"
              class="vendor_enter_bill-glass-status vendor_enter_bill-glass-status--loading vendor-enter-bill-timesheet-loading"
              role="status"
              aria-live="polite"
            >
              <i class="bi bi-arrow-repeat vendor_enter_bill-glass-status-spin" aria-hidden="true"></i>
              <span>Loading consultant timesheets…</span>
            </div>
            <div
              v-else-if="!consultantsLoading && vendorId"
              class="vendor_enter_bill-glass-status vendor_enter_bill-glass-status--metrics vendor-enter-bill-timesheet-count"
            >
              <i class="bi bi-bar-chart-line" aria-hidden="true"></i>
              <div class="vendor_enter_bill-glass-status-body">
                <span class="vendor_enter_bill-glass-metric">
                  <span class="vendor_enter_bill-glass-metric-label">Available timesheets</span>
                  <span class="vendor_enter_bill-glass-metric-value">{{ availableConsultantCount }}</span>
                </span>
                <span class="vendor_enter_bill-glass-metric-divider" aria-hidden="true"></span>
                <span class="vendor_enter_bill-glass-metric">
                  <span class="vendor_enter_bill-glass-metric-label">Added to bill</span>
                  <span class="vendor_enter_bill-glass-metric-value">{{ addedConsultantCount }}</span>
                </span>
                <span
                  v-if="consultantTimesheets.length > 0 && availableConsultantCount === 0"
                  class="vendor_enter_bill-glass-status-note vendor-enter-bill-timesheet-all-added"
                >
                  All timesheets auto-added
                </span>
                <span
                  v-else-if="availableConsultantCount > 0"
                  class="vendor_enter_bill-glass-status-note vendor-enter-bill-timesheet-remaining"
                >
                  {{ availableConsultantCount }} remaining to add
                </span>
              </div>
            </div>
          </div>

        <div v-if="!vendorId" class="vendor_enter_bill-glass-empty vendor-enter-bill-no-vendor-message vendor-enter-bill-no-vendor-message--compact">
          <p>Select a vendor to load consultant timesheet records.</p>
        </div>
        <div v-else-if="consultantTimesheets.length === 0" class="vendor_enter_bill-glass-empty vendor-enter-bill-no-vendor-message vendor-enter-bill-no-vendor-message--compact">
          <div class="vendor-enter-bill-no-vendor-icon" aria-hidden="true"><i class="bi bi-clock-history"></i></div>
          <h3>No Consultant Hours</h3>
          <p>This vendor has no consultant timesheet records available for billing.</p>
        </div>
        <template v-else>
          <div class="consultant-glass-table-section vendor_enter_bill-table-section">
            <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
              <div class="consultant-glass-table-scroll-x">
          <table class="consultant-glass-table vendor_enter_bill-itemize-table">
            <thead>
              <tr>
                <th>Consultant</th>
                <th>Description</th>
                <th>Hours</th>
                <th>Hourly Rate (USD)</th>
                <th>Timesheet</th>
                <th>Total (USD)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(consultant, idx) in consultantTimesheets" :key="idx" class="consultant-glass-table-row">
                <td>
                  <select v-if="consultantOptions.length > 0" v-model="consultant.name" class="vendor_enter_bill-itemize-table-select" @change="onConsultantChange(idx)">
                    <option value="">Select Consultant</option>
                    <option
                      v-for="opt in consultantOptions.filter(opt => !consultantTimesheets.some((c, i) => c.name === opt.name && c.timesheetId === opt.timesheetId && i !== idx))"
                      :key="`${opt.name}-${opt.timesheetId || 'no-timesheet'}`"
                      :value="opt.name"
                    >
                      {{ opt.name }} ({{ opt.description }})
                    </option>
                  </select>
                  <input v-else v-model="consultant.name" type="text" placeholder="Consultant Name" class="vendor_enter_bill-itemize-table-input" />
                </td>
                <td>
                  <input v-model="consultant.description" type="text" placeholder="Description" class="vendor_enter_bill-itemize-table-input" />
                </td>
                <td>
                  <input v-model.number="consultant.hours" type="number" min="0" placeholder="Hours" class="vendor_enter_bill-itemize-table-input" />
                </td>
                <td>
                  <span>{{ consultant.hourlyRate ? '$' + consultant.hourlyRate : '- or 0' }}</span>
                </td>
                <td>
                  <div v-if="consultant.attachments && consultant.attachments.length > 0" class="vendor-enter-bill-attachment-links">
                    <a
                      v-for="(attachment, attIdx) in consultant.attachments"
                      :key="attIdx"
                      :href="getAttachmentUrl(attachment.filePath || attachment)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="vendor-enter-bill-attachment-link"
                    >
                      <i class="bi bi-paperclip"></i> {{ attachment.fileName || attachment }}
                    </a>
                  </div>
                  <span v-else-if="consultant.attachment">
                    <a :href="getAttachmentUrl(consultant.attachment)" target="_blank" rel="noopener noreferrer" class="vendor-enter-bill-attachment-link">View Timesheet</a>
                  </span>
                  <span v-else>-</span>
                </td>
                <td>${{ getConsultantTotal(consultant) }}</td>
              </tr>
              <tr>
                <td colspan="6" class="vendor_enter_bill-table-total-row">
                  Total (USD): <span class="vendor_enter_bill-total-value">$ {{ consultantTimesheetTotal.toFixed(2) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
              </div>
            </div>
          </div>
        </template>
              </div>

              <div v-show="activeTab === 'product'" class="vendor_enter_bill-itemize-section">
          <div class="vendor_enter_bill-glass-hint vendor-enter-bill-item-filter-info">
            <i class="bi bi-info-circle" aria-hidden="true"></i>
            <small v-if="vendorId">Showing vendor product/service items from the selected vendor.</small>
            <small v-else>Select a vendor to see their product/service items.</small>
          </div>

          <div class="consultant-glass-table-section vendor_enter_bill-table-section">
            <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
              <div class="consultant-glass-table-scroll-x">
          <table class="consultant-glass-table vendor_enter_bill-itemize-table">
            <thead>
              <tr>
                <th>Item Type</th>
                <th>Item Name</th>
                <th>Description</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(row, idx) in productRows" :key="'product-' + idx">
                <!-- Main product/service row -->
                <tr v-if="row.itemId && row.itemType" class="consultant-glass-table-row">
                  <td>
                    <span v-if="row.itemType === 'product'"><i class="bi bi-box-seam vendor_enter_bill-item-type-icon" aria-hidden="true"></i> Product</span>
                    <span v-else-if="row.itemType === 'service'"><i class="bi bi-tools vendor_enter_bill-item-type-icon" aria-hidden="true"></i> Service</span>
                    <span v-else-if="row.itemType === 'Inventory'"><i class="bi bi-boxes vendor_enter_bill-item-type-icon" aria-hidden="true"></i> Inventory</span>
                    <span v-else>{{ row.itemType }}</span>
                  </td>
                  <td>
                    <select v-model="row.selectedItem" class="vendor_enter_bill-itemize-table-select" @change="onProductServiceChange(row)">
                      <option value="">Select</option>
                      <option v-for="item in getAvailableItemsForRow(idx)" :key="item.type + '-' + item.id" :value="JSON.stringify({ id: item.id, type: item.type })">
                        {{ item.name }} - {{ item.type }}
                      </option>
                    </select>
                  </td>
                  <td><input type="text" v-model="row.description" class="vendor_enter_bill-itemize-table-input" /></td>
                  <td><input type="number" v-model="row.quantity" class="vendor_enter_bill-itemize-table-input" /></td>
                  <td><input type="number" v-model="row.unitPrice" class="vendor_enter_bill-itemize-table-input" /></td>
                  <td>{{ row.quantity && row.unitPrice ? (row.quantity * row.unitPrice).toFixed(2) : '' }}</td>
                </tr>
                
              </template>
              
              <!-- Empty row for adding new items -->
              <tr v-if="productRows.length === 0 || productRows.every(row => !row.itemId)">
                <td colspan="6" style="text-align: center; color: #666; font-style: italic;">
                  No product/service items selected. Use the dropdown above to add items.
                </td>
              </tr>
              
              <tr>
                <td colspan="6" class="vendor_enter_bill-table-total-row">Total (USD): <span class="vendor_enter_bill-total-value">$ {{ productServiceTotal.toFixed(2) }}</span></td>
              </tr>
            </tbody>
          </table>
              </div>
            </div>
          </div>
          <div v-if="showAddProductButton" class="vendor_enter_bill-itemize-add-wrap">
            <PrimaryButton label="Add New Item" icon="bi bi-plus-lg" @click="addProductRow" />
          </div>
              </div>
            </div>

            <div class="vendor_enter_bill-glass-panel vendor_enter_bill-comments-glass">
              <div class="vendor_enter_bill-form-group vendor_enter_bill-form-group--label-description">
        <LabelDescriptionField
          input-id="enter-bill-comments"
          label="Write comments for this Enter Bill."
          v-model="comments"
          placeholder="Comments"
          flex
        />
              </div>
            </div>

      <!-- Attachments Section (reference: CustomerAddNewInvoice.vue) -->
      <div ref="billAttachmentsSection" class="vendor_enter_bill-glass-panel vendor_enter_bill-attachments-section glass-tab-panel--nested">
        <div class="vendor_enter_bill-attachments-header">
          <i class="bi bi-paperclip" aria-hidden="true"></i>
          <span>Attachments</span>
        </div>
        <div v-if="attachments && attachments.length > 0" class="vendor_enter_bill-attachments-list">
          <div
            v-for="(attachment, index) in attachments"
            :key="index"
            :class="['vendor_enter_bill-attachment-item', { 'vendor_enter_bill-attachment-item--compressing': compressingAttachmentIndex === index }]"
          >
            <span :class="['vendor_enter_bill-attachment-icon', `vendor_enter_bill-attachment-icon--${attachmentFileKind(attachment)}`]" aria-hidden="true">
              <i :class="attachmentFileIcon(attachment)"></i>
            </span>
            <div class="vendor_enter_bill-attachment-info">
              <a href="#" @click.prevent="viewBillAttachment(attachment)" class="vendor_enter_bill-attachment-link" :title="attachment.fileName">{{ attachment.fileName }}</a>
              <div class="vendor_enter_bill-attachment-meta">
                <span class="vendor_enter_bill-attachment-size">{{ formatFileSize(attachment.fileSize) }}</span>
                <span
                  v-if="compressingAttachmentIndex === index"
                  class="vendor_enter_bill-compress-status vendor_enter_bill-compress-status--compressing"
                >
                  <i class="bi bi-arrow-repeat vendor_enter_bill-compress-spin" aria-hidden="true"></i>
                  Compressing…
                </span>
                <span v-else-if="attachmentCompressQueue.includes(attachment)" class="vendor_enter_bill-compress-status">
                  Waiting…
                </span>
                <span
                  v-else-if="attachment.compressionResult"
                  :class="['vendor_enter_bill-compress-status', `vendor_enter_bill-compress-status--${attachment.compressionResult}`]"
                >
                  {{ compressResultLabel(attachment) }}
                </span>
              </div>
            </div>
            <div class="vendor_enter_bill-attachment-buttons">
              <button
                v-if="canCompressAttachment(attachment) && compressingAttachmentIndex !== index && !attachmentCompressQueue.includes(attachment)"
                type="button"
                class="vendor_enter_bill-compress-file-btn"
                title="Compress this file"
                :disabled="compressingAttachments"
                @click="compressBillAttachment(index)"
              >
                <i class="bi bi-file-earmark-zip" aria-hidden="true"></i>
                Compress
              </button>
              <button type="button" @click="removeBillAttachment(index)" class="vendor_enter_bill-attachment-delete-btn" title="Remove attachment" aria-label="Remove attachment" :disabled="compressingAttachments">
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="vendor_enter_bill-attachment-actions">
          <label for="enter-bill-attachment-input" class="vendor_enter_bill-attach-file-link">
            Attach a file
            <input
              id="enter-bill-attachment-input"
              type="file"
              ref="addBillFileInput"
              @change="handleBillFileUpload"
              multiple
            />
          </label>
          <span v-if="attachmentCompressNote" class="vendor_enter_bill-compress-note">{{ attachmentCompressNote }}</span>
        </div>
      </div>

      <div class="vendor_enter_bill-glass-panel vendor_enter_bill-total-section glass-tab-panel--nested">
        <span>Total (USD)</span>
        <span class="vendor_enter_bill-total-value">$ {{ finalTotal.toFixed(2) }}</span>
      </div>
          </div>
        </div>
      </div>

      <div class="vendor_enter_bill-form-actions glass-tab-view__actions">
        <SecondaryButton label="Cancel" icon="bi bi-x-lg" :disabled="enterBillSubmitDisabled" @click="handleCancel" />
        <PrimaryButton label="Save and New" icon="bi bi-plus-lg" :disabled="enterBillSubmitDisabled" @click="handleSaveAndNew" />
        <PrimaryButton label="Save and Close" icon="bi bi-check-lg" :disabled="enterBillSubmitDisabled" @click="handleSaveAndClose" />
      </div>
    </form>
    <AddNewPaymentTerm
      :visible="showAddPaymentTermModal"
      :companyId="$route.params.id"
      @ok="onAddPaymentTermOk"
      @cancel="showAddPaymentTermModal = false"
    />
    <SuccessModal
      :show="successModalVisible"
      :title="successModalTitle"
      :message="successModalMessage"
      :buttonText="successModalButtonText"
      @close="successModalVisible = false"
    />
    
    <!-- Error Modal for validation -->
    <ErrorModal
      :show="showErrorModal"
      :title="errorModalTitle"
      :message="errorModalMessage"
      :buttonText="errorModalButtonText"
      :action-button-text="errorModalActionButtonText"
      @close="onErrorModalClose"
      @action="onErrorModalAction"
    />
    
  </div>
</template>

<script>
import '../styles/EnterBill.css';
import ApiService from '@/services/api.service.js';
import logger from '@/utils/logger';
import { notifyCompanyBillSaved } from '@/services/billCompanyNotification.service.js';
import AddNewPaymentTerm from '@/modules/company/views/AddNewPaymentTerm.vue';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import BillJournalEntry from '@/modules/company/views/BillJournalEntry.vue';
import CreateBillProgressor from '@/components/CreateBillProgressor.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelDescriptionField from '@/modules/consultant/components/LabelDescriptionField.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import {
  attachmentsExceedBillLimit,
  buildBillAttachmentLimitMessage,
  describeAttachmentCompression,
  getAttachmentsTotalBytes,
  resolveBillSaveAlert,
} from '@/modules/vendor/utils/billSaveAlert.js';
import { compressSingleAttachment, isCompressibleAttachment } from '@/modules/vendor/utils/attachmentCompression.js';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import { isConsultantIndustry as isConsultantIndustryId } from '@/constants/companyIndustryTypes';

export default {
  name: 'EnterBill',
  components: {
    AddNewPaymentTerm,
    BrucdcumHeader,
    SuccessModal,
    ErrorModal,
    BillJournalEntry,
    CreateBillProgressor,
    LabelInputField,
    LabelDropdown,
    LabelDescriptionField,
    LabelDateField,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      activeTab: 'product',
      vendorId: '',
      contactName: '',
      phone: '',
      paymentTerm: '',
      paymentTermsList: [],
      previousPaymentTerm: '',
      showAddPaymentTermModal: false,
      billDate: '',
      dueDate: '',
      deliveryDate: '',
      invNo: '',
      serialNo: 1,
      vendorList: [],
      contactList: [],
      accountList: [],
      allCompanyAccounts: [],
      productServiceList: [],
      vendorBills: [],
      showAccountItems: true,
      showProductItems: true,
      accountRows: [ { accountId: '', description: '', amount: 0 } ],
      productRows: [ { itemId: '', itemType: '', selectedItem: '', description: '', quantity: 0, unitPrice: 0 } ],
      comments: '',
      payFrom: '',
      payFromList: [],
      payFromBalanceError: '',
      // Consultant timesheet properties
      showConsultantTimesheets: true,
      consultantsLoading: false,
      consultantsError: '',
      consultantOptions: [],
      consultantTimesheets: [],
      allConsultantOptions: [], // Store all options for "Select All" functionality
      expenseAccountMappings: [], // Store account mappings with type information for amount calculations
      // Filter by specific consultant (from ConsultantCenter)
      filterConsultantId: null, // Store consultant ID to filter by
      // Filter by specific timesheet (from ConsultantPreviewList - Create Bill button)
      filterTimesheetId: null, // Store timesheet ID to filter by
      consultantCustomerServiceId: null, // Store consultantCustomerServiceId for multi-customer consultants
      // Store originally pre-filled vendor ID to track if user changed it
      prefillVendorId: null, // Store the originally pre-filled vendor ID
      // Success modal properties
      successModalVisible: false,
      successModalTitle: 'Success',
      successModalMessage: '',
      successModalButtonText: 'OK',
      // Error modal properties
      showErrorModal: false,
      errorModalTitle: 'Validation Error',
      errorModalMessage: '',
      errorModalButtonText: 'OK',
      errorModalActionKey: null,
      errorModalActionButtonText: '',
      journalAutoCreateActive: false,
      enterBillPipeline: null,
      enterBillProgressorShow: false,
      enterBillProgressorSteps: [],
      enterBillProgressorOkEnabled: false,
      enterBillProgressorFootMessage: '',
      enterBillProgressorFootTone: 'neutral',
      savedBillId: null, // Store the saved bill ID for journal entry reference
      // Bill-level attachments (reference: CustomerAddNewInvoice.vue)
      attachments: [],
      attachmentPreviewUrls: {},
      compressingAttachmentIndex: null,
      attachmentCompressQueue: [],
      attachmentCompressNote: '',
    };
  },
  computed: {
    compressingAttachments() {
      return this.compressingAttachmentIndex !== null || this.attachmentCompressQueue.length > 0;
    },
    effectiveIndustryId() {
      const company = this.$store?.state?.currentCompany;
      return company?.industryId ?? company?.cp_industry_type;
    },
    isConsultantIndustry() {
      return isConsultantIndustryId(this.effectiveIndustryId);
    },
    vendorDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.activeVendors || []).map((vendor) => ({
          value: String(vendor.id),
          label: vendor.vendorName,
        })),
        { value: '__create_new__', label: 'Create new Vendor' },
      ];
    },
    contactDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.contactList || []).map((contact) => ({
          value: contact.name,
          label: `${contact.name}${contact.email ? ` (${contact.email})` : ''}${contact.primary ? ' [Primary]' : ''}`,
        })),
      ];
    },
    paymentTermDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...(this.paymentTermsList || []).map((term) => ({
          value: String(term.id),
          label: `${term.name} (${term.description})`,
        })),
        { value: '__add_new__', label: 'Add New Payment Term' },
      ];
    },
    // Check if there are any source records (consultant timesheets or product/service items)
    hasSourceRecords() {
      const hasConsultants =
        this.isConsultantIndustry &&
        this.consultantTimesheets &&
        this.consultantTimesheets.length > 0;
      const hasProducts = this.productServiceList && this.productServiceList.length > 0;
      return hasConsultants || hasProducts;
    },
    // Active vendors only (excludes inactive)
    activeVendors() {
      return (this.vendorList || []).filter(v => v.active !== false);
    },
    // Create individual account rows for each Product/Service item and Consultant timesheet
    visibleAccountRows() {
      const individualRows = [];
      
      // Add individual rows for each Product/Service item
      this.productRows.forEach((row, rowIndex) => {
        if (row.itemId && row.itemType && row.quantity && row.unitPrice) {
          const quantity = Number(row.quantity) || 0;
          const unitPrice = Number(row.unitPrice) || 0;
          const amount = quantity * unitPrice;
          
          if (amount > 0) {
            if (row.itemType === 'Inventory') {
              const inventoryItem = this.productServiceList.find(
                i => i.type === 'Inventory' && String(i.id) === String(row.itemId)
              );
              if (inventoryItem && inventoryItem.assetsAccountId) {
                individualRows.push({
                  accountId: inventoryItem.assetsAccountId,
                  description: `Inventory Purchase - ${row.description || inventoryItem.name || 'Unnamed Item'}`,
                  amount: amount,
                  itemType: row.itemType,
                  itemName: row.description || inventoryItem.name || 'Unnamed Item',
                  quantity: quantity,
                  unitPrice: unitPrice,
                  rowIndex: rowIndex,
                  source: `Product/Service Row ${rowIndex + 1}`
                });
              }
              return;
            }

            // Find the expense account for this item
            const mapping = this.expenseAccountMappings.find(m => 
              (m.type === 'product' || m.type === 'service') && 
              ((m.type === 'product' && m.productItemId === row.itemId) || 
               (m.type === 'service' && m.serviceItemId === row.itemId))
            );
            
            if (mapping) {
              individualRows.push({
                accountId: mapping.accountId,
                description: `${row.itemType} Expense - ${row.description || 'Unnamed Item'}`,
                amount: amount,
                itemType: row.itemType,
                itemName: row.description || 'Unnamed Item',
                quantity: quantity,
                unitPrice: unitPrice,
                rowIndex: rowIndex,
                source: `Product/Service Row ${rowIndex + 1}`
              });
            }
          }
        }
      });
      
      // Add individual rows for each Consultant timesheet (consultant industries only)
      if (this.isConsultantIndustry) {
        this.consultantTimesheets.forEach((consultant, consultantIndex) => {
          if (consultant.timesheetId) {
            const hours = Number(consultant.hours) || 0;
            const hourlyRate = Number(consultant.hourlyRate) || 0;
            const amount = hours * hourlyRate;
            
            if (amount > 0) {
              // For multi-customer consultants, use expenseAccountId from ConsultantCustomerService
              // Otherwise, find the expense account from mappings
              let expenseAccountId = null;
              
              if (consultant.expenseAccountId) {
                // Use expenseAccountId from ConsultantCustomerService (for multi-customer consultants)
                expenseAccountId = consultant.expenseAccountId;
                logger.log('Using expenseAccountId from ConsultantCustomerService for consultant timesheet:', {
                  consultantId: consultant.id,
                  consultantName: consultant.name,
                  expenseAccountId: expenseAccountId,
                  consultantCustomerServiceId: consultant.consultantCustomerServiceId
                });
              } else {
                // Fallback: Find the expense account for this consultant from mappings
                const mapping = this.expenseAccountMappings.find(m => 
                  m.type === 'consultant' && m.consultantId === consultant.id
                );
                
                if (mapping) {
                  expenseAccountId = mapping.accountId;
                }
              }
              
              if (expenseAccountId) {
                individualRows.push({
                  accountId: expenseAccountId,
                  description: `Consultant Expense - ${consultant.name}`,
                  amount: amount,
                  itemType: 'consultant',
                  itemName: consultant.name,
                  hours: hours,
                  hourlyRate: hourlyRate,
                  consultantIndex: consultantIndex,
                  source: `Consultant Timesheet ${consultantIndex + 1}`,
                  consultantCustomerServiceId: consultant.consultantCustomerServiceId || null // Store for reference
                });
              } else {
                logger.warn('No expense account found for consultant timesheet:', {
                  consultantId: consultant.id,
                  consultantName: consultant.name,
                  consultantCustomerServiceId: consultant.consultantCustomerServiceId
                });
              }
            }
          }
        });
      }
      
      return individualRows;
    },
    accountTotal() {
      return this.visibleAccountRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
    },
    productServiceTotal() {
      if (!this.productRows || !Array.isArray(this.productRows)) {
        return 0;
      }
      return this.productRows.reduce((sum, row) => {
        if (row && row.quantity != null && row.unitPrice != null) {
          return sum + ((Number(row.quantity) || 0) * (Number(row.unitPrice) || 0));
        }
        return sum;
      }, 0);
    },
    consultantTimesheetTotal() {
      if (!this.isConsultantIndustry) return 0;
      return this.consultantTimesheets.reduce((sum, consultant) => {
        const hours = Number(consultant.hours) || 0;
        const hourlyRate = Number(consultant.hourlyRate) || 0;
        return sum + (hours * hourlyRate);
      }, 0);
    },
    finalTotal() {
      // Since account amounts are now automatically calculated from consultant and product/service totals,
      // we only need to use accountTotal to avoid double-counting
      return this.accountTotal;
    },
    // Count of available consultant timesheets (not yet added to bill)
    availableConsultantCount() {
      return this.getAvailableConsultantOptions().length;
    },
    // Count of consultant timesheets added to bill
    addedConsultantCount() {
      return this.consultantTimesheets.filter(c => c.timesheetId).length;
    },
    // Check if bill has minimum data for journal entry
    hasMinimumDataForJournal() {
      // For journal entry, we need at least vendor and date
      return this.vendorId && this.billDate;
    },
    // Prepare bill data for journal entry modal
    billDataForJournal() {
      // Service Date automatically uses Bill Date
      const deliveryDateToUse = this.deliveryDate || this.billDate;
      return {
        billId: this.savedBillId, // Include saved bill ID for journal entry reference
        billNo: this.invNo,
        billDate: this.billDate,
        date: this.billDate,
        dueDate: this.dueDate,
        deliveryDate: deliveryDateToUse,
        vendorName: this.selectedVendor?.vendorName || 'Vendor',
        vendor: this.selectedVendor,
        totalAmount: this.finalTotal,
        subtotal: this.accountTotal,
        comments: this.comments,
        accountRecords: this.visibleAccountRows
      };
    },
    selectedVendor() {
      return this.vendorList.find(v => String(v.id) === String(this.vendorId));
    },
    selectedPaymentTermDays() {
      const selectedTerm = this.paymentTermsList.find(t => String(t.id) === String(this.paymentTerm));
      if (selectedTerm && selectedTerm.dueDays != null && !isNaN(Number(selectedTerm.dueDays))) {
        return Number(selectedTerm.dueDays);
      }
      return null;
    },
    showAddProductButton() {
      // Show add button only when:
      // 1. No vendor selected, OR
      // 2. Vendor selected but not all vendor items are populated in rows
      
      if (!this.vendorId) {
        return true; // Always show when no vendor selected
      }
      
      // If vendor selected, check if all vendor items are already in rows
      const vendorItemsCount = this.productServiceList.length;
      const populatedRowsCount = this.productRows.filter(row => 
        row.itemId && row.itemType && row.selectedItem
      ).length;
      
      // Show button only if there are items available but not all are added
      return vendorItemsCount > populatedRowsCount;
    },
    // Show pre-selected message only if vendor matches the originally pre-filled vendor
    showPrefillMessage() {
      if (!this.$route.query.vendorName || !this.vendorId || !this.prefillVendorId) {
        return false;
      }
      // Only show if current vendor matches the originally pre-filled vendor
      return String(this.vendorId) === String(this.prefillVendorId);
    },
    enterBillSubmitDisabled() {
      return (
        (this.enterBillProgressorShow && !this.enterBillProgressorOkEnabled) ||
        this.journalAutoCreateActive ||
        this.compressingAttachments
      );
    },
  },
  async created() {
    // Initialize billDate with current date
    this.billDate = this.formatDateForDatePicker(new Date());
    // Service Date automatically uses Bill Date
    this.deliveryDate = this.billDate;
    
    try {
      const companyId = this.$route.params.id;
      const data = await ApiService.getAllVendors(companyId);
      this.vendorList = Array.isArray(data) ? data : [];
    } catch (err) {
      this.vendorList = [];
    }
    
    // Prefill vendor if coming from VendorPreviewList
    if (this.$route.query.prefillVendor) {
      this.vendorId = this.$route.query.prefillVendor;
      this.prefillVendorId = this.$route.query.prefillVendor; // Store original pre-filled vendor ID
    }
    
    // Check if filtering by specific consultant (from ConsultantCenter)
    if (this.isConsultantIndustry) {
      if (this.$route.query.consultantId && this.$route.query.filterByConsultant === 'true') {
        this.filterConsultantId = this.$route.query.consultantId;
        this.activeTab = 'consultant';
        logger.log('Filtering by consultant ID:', this.filterConsultantId);
      } else if (
        this.$route.query.activeTab === 'consultant' ||
        this.$route.query.filterByConsultant === 'true'
      ) {
        this.activeTab = 'consultant';
      } else {
        this.activeTab = 'consultant';
      }
    } else {
      this.activeTab = this.$route.query.activeTab === 'account' ? 'account' : 'product';
    }
    
    // Check if filtering by specific timesheet (from ConsultantPreviewList - Create Bill button)
    if (this.$route.query.timesheetId) {
      this.filterTimesheetId = this.$route.query.timesheetId;
      logger.log('Filtering by timesheet ID:', this.filterTimesheetId);
    }
    
    // Store consultantCustomerServiceId if provided (for multi-customer consultants)
    if (this.$route.query.consultantCustomerServiceId) {
      this.consultantCustomerServiceId = this.$route.query.consultantCustomerServiceId;
      logger.log('Stored consultantCustomerServiceId from query params:', this.consultantCustomerServiceId);
      
      // If consultantCustomerServiceId is provided, fetch it to get preferredVendorId
      // This is needed for multi-customer consultants where preferredVendorId comes from ConsultantCustomerService
      try {
        logger.log('Fetching ConsultantCustomerService to get preferredVendorId:', this.consultantCustomerServiceId);
        const serviceRes = await ApiService.get(`/customer/consultant-customer-services/${this.consultantCustomerServiceId}`);
        const service = serviceRes.data;
        
        if (service && service.preferredVendorId) {
          // Use preferredVendorId from ConsultantCustomerService for multi-customer consultants
          // This ensures we're using the correct vendor for this specific customer service
          const serviceVendorId = service.preferredVendorId;
          
          // Set vendorId from ConsultantCustomerService if not already set from prefillVendor
          // Or if prefillVendor doesn't match, use the one from ConsultantCustomerService
          if (!this.vendorId || (this.vendorId && String(this.vendorId) !== String(serviceVendorId))) {
            logger.log('Setting vendorId from ConsultantCustomerService.preferredVendorId:', serviceVendorId);
            this.vendorId = serviceVendorId;
            this.prefillVendorId = serviceVendorId; // Store as pre-filled vendor ID
            
            // Also update vendorName if available
            const vendor = this.vendorList.find(v => String(v.id) === String(serviceVendorId));
            if (vendor) {
              // vendorName will be set when we process the vendor below
            }
          }
        }
      } catch (error) {
        logger.warn('Could not fetch ConsultantCustomerService for preferredVendorId, using prefillVendor if available:', error);
      }
    }
    
    await this.fetchPaymentTerms();
    await this.fetchAccounts();
    await this.fetchProductServiceItems();
    
    // If vendor is pre-filled, fetch consultants and timesheets immediately
    // This ensures timesheets are loaded even if watcher hasn't triggered yet
    if (this.vendorId) {
      const selected = this.vendorList.find(v => String(v.id) === String(this.vendorId));
      if (selected) {
        this.contactList = selected.contacts || [];
        // Auto-select contact
        if (this.contactList.length > 0) {
          const primaryContact = this.contactList.find(contact => contact.primary);
          if (primaryContact) {
            this.contactName = primaryContact.name;
            this.phone = primaryContact.phone || '';
          } else {
            this.contactName = this.contactList[0].name;
            this.phone = this.contactList[0].phone || '';
          }
        }
        // Fetch expense accounts and consultants for pre-filled vendor
        await this.fetchExpenseAccounts(this.vendorId);
        await this.fetchConsultantsForVendor(this.vendorId);
      }
      if (this.vendorId) {
        this.applyDefaultItemizeTab();
      }
    }
    // Fetch payFromList for balance validation
    const companyId = this.$route.params.id;
    const res = await ApiService.getCompanyAccounts(companyId);
    this.payFromList = (res.data.accounts || res.data || []).filter(a => [
      'Cash', 'Bank', 'Paypal', 'Credit Card', 'Other current Asset'
    ].includes(a.accountType));
  },
  methods: {
    showSuccessModal(title, message, buttonText = 'OK') {
      this.successModalTitle = title;
      this.successModalMessage = message;
      this.successModalButtonText = buttonText;
      this.successModalVisible = true;
    },
    showAlertModal(title, message, buttonText = 'OK', actionKey = null) {
      this.errorModalTitle = title || 'Alert';
      this.errorModalMessage = message || '';
      this.errorModalButtonText = buttonText;
      this.errorModalActionKey = actionKey || null;
      this.errorModalActionButtonText = '';
      this.showErrorModal = true;
    },
    showBillSaveAlert(err) {
      const alert = resolveBillSaveAlert(err, { attachments: this.attachments });
      this.showAlertModal(alert.title, alert.message, 'OK', alert.actionKey);
      this.errorModalActionButtonText = alert.actionButtonText || '';
    },
    onErrorModalClose() {
      this.showErrorModal = false;
      this.errorModalActionKey = null;
      this.errorModalActionButtonText = '';
    },
    onErrorModalAction() {
      const actionKey = this.errorModalActionKey;
      this.onErrorModalClose();
      if (actionKey === 'compress-files') {
        this.compressBillAttachmentsForReview();
        return;
      }
      const companyId = this.$route.params.id;
      if (!companyId || !this.$router) return;
      if (actionKey === 'drafts-captured') {
        this.$router.push({
          name: 'DraftBills',
          params: { id: String(companyId) },
          query: { tab: 'drafts' },
        }).catch(() => {});
        return;
      }
      if (actionKey === 'invites-submitted') {
        this.$router.push({
          name: 'VendorPortalHub',
          params: { id: String(companyId) },
        }).catch(() => {});
      }
    },
    async fetchPaymentTerms() {
      const companyId = this.$route.params.id;
      const termsRes = await ApiService.get(`/company/payment-terms?companyId=${companyId}`);
      this.paymentTermsList = Array.isArray(termsRes.data.paymentTerms) ? termsRes.data.paymentTerms : [];
    },
    handlePaymentTermChange(valueOrEvent) {
      const selected =
        valueOrEvent && typeof valueOrEvent === 'object' && valueOrEvent.target
          ? valueOrEvent.target.value
          : valueOrEvent;
      if (selected === '__add_new__') {
        this.showAddPaymentTermModal = true;
        this.$nextTick(() => {
          this.paymentTerm = this.previousPaymentTerm;
        });
      } else {
        this.paymentTerm = selected;
        this.previousPaymentTerm = selected;
      }
    },
    async onAddPaymentTermOk(newTerm) {
      this.showAddPaymentTermModal = false;
      await this.fetchPaymentTerms();
      if (newTerm && newTerm.id) {
        this.paymentTerm = String(newTerm.id);
        this.previousPaymentTerm = String(newTerm.id);
        this.showSuccessModal('Success', 'Payment term created!');
      }
    },
    async fetchAccounts() {
      const companyId = this.$route.params.id;
      const res = await ApiService.getCompanyAccounts(companyId);
      const allAccounts = res.data.accounts || res.data || [];
      const allowedTypes = [
        'Expense',
        'Other Expense',
        'Cost of Goods Sold',
        'Fixed Asset',
        'Current Liability',
        'Payroll Liability'
      ];
      this.allCompanyAccounts = Array.isArray(allAccounts) ? allAccounts : [];
      this.accountList = allAccounts.filter(a => allowedTypes.includes(a.accountType));
    },
    async fetchProductServiceItems(vendorId = null) {
      const companyId = this.$route.params.id;
      
      let vendorProductList = [];
      let vendorServiceList = [];
      let vendorInventoryList = [];
      
      // Always use vendor-specific API only
      // Note: Backend automatically filters to only show items with billStatus = 'Available'
      if (vendorId) {
        // Fetch vendor-specific product items using by-vendor API
        try {
          const productRes = await ApiService.get(`/vendor/product-items/by-vendor/${vendorId}?companyId=${companyId}`);
          vendorProductList = (productRes.data.data || []).map(item => ({
            id: item.id,
            name: item.productName,
            type: item.type,
            salesPrice: item.salesPrice,
            purchasePrice: item.purchasePrice,
            preferredVendor: vendorId
          }));
        } catch (err) {
          logger.warn(`No vendor products found for vendor ${vendorId}:`, err.message);
          vendorProductList = [];
        }

        // Fetch vendor-specific service items using by-vendor API
        try {
          const serviceRes = await ApiService.get(`/vendor/service-items/by-vendor/${vendorId}?companyId=${companyId}`);
          vendorServiceList = (serviceRes.data.data || []).map(item => ({
            id: item.id,
            name: item.serviceName,
            type: item.type,
            salesPrice: item.salesPrice,
            purchasePrice: item.purchasePrice,
            preferredVendor: vendorId
          }));
        } catch (err) {
          logger.warn(`No vendor services found for vendor ${vendorId}:`, err.message);
          vendorServiceList = [];
        }

        try {
          const inventoryRes = await ApiService.get(`/inventory/companies/${companyId}/items`);
          const inventoryItems = Array.isArray(inventoryRes.data) ? inventoryRes.data : [];
          vendorInventoryList = inventoryItems
            .filter(item => item.active !== false && String(item.vendorId || '') === String(vendorId))
            .map(item => ({
              id: item.id,
              name: item.name,
              type: 'Inventory',
              salesPrice: item.salesPrice,
              purchasePrice: item.purchasePrice,
              preferredVendor: vendorId,
              assetsAccountId: item.assetsAccount || null
            }));
        } catch (err) {
          logger.warn(`No vendor inventory items found for vendor ${vendorId}:`, err.message);
          vendorInventoryList = [];
        }
      } else {
        // No vendor selected - clear product rows and show default empty row
        this.productRows = [{ itemId: '', itemType: '', selectedItem: '', description: '', quantity: 0, unitPrice: 0 }];
      }
      
      // Set product/service list to only vendor-specific items
      this.productServiceList = [...vendorProductList, ...vendorServiceList, ...vendorInventoryList];
      
      // Auto-populate product/service rows if vendor has specific items
      if (vendorId) {
        if (vendorProductList.length > 0 || vendorServiceList.length > 0 || vendorInventoryList.length > 0) {
          this.autoPopulateProductServiceRows(vendorProductList, vendorServiceList, vendorInventoryList);
        } else {
          // Vendor selected but has no specific items - show default empty row
          this.productRows = [{ itemId: '', itemType: '', selectedItem: '', description: '', quantity: 0, unitPrice: 0 }];
        }
      }
    },
    autoPopulateProductServiceRows(vendorProductList, vendorServiceList, vendorInventoryList = []) {
      // Add safety checks for input parameters
      if (!vendorProductList || !Array.isArray(vendorProductList)) {
        vendorProductList = [];
      }
      if (!vendorServiceList || !Array.isArray(vendorServiceList)) {
        vendorServiceList = [];
      }
      if (!Array.isArray(vendorInventoryList)) {
        vendorInventoryList = [];
      }
      
      // Initialize productRows array if not exists
      if (!this.productRows || !Array.isArray(this.productRows)) {
        this.productRows = [];
      }
      
      // Clear existing product rows
      this.productRows = [];
      
      // Create rows for each vendor-specific product item
      vendorProductList.forEach(product => {
        if (product && product.id && product.type) {
          this.productRows.push({
            itemId: product.id,
            itemType: product.type,
            selectedItem: JSON.stringify({ id: product.id, type: product.type }),
            description: product.name || '',
            quantity: 1, // Default quantity
            unitPrice: product.purchasePrice || product.salesPrice || 0
          });
        }
      });
      
      // Create rows for each vendor-specific service item
      vendorServiceList.forEach(service => {
        if (service && service.id && service.type) {
          this.productRows.push({
            itemId: service.id,
            itemType: service.type,
            selectedItem: JSON.stringify({ id: service.id, type: service.type }),
            description: service.name || '',
            quantity: 1, // Default quantity
            unitPrice: service.purchasePrice || service.salesPrice || 0
          });
        }
      });

      vendorInventoryList.forEach(inventoryItem => {
        if (inventoryItem && inventoryItem.id) {
          this.productRows.push({
            itemId: inventoryItem.id,
            itemType: inventoryItem.type,
            selectedItem: JSON.stringify({ id: inventoryItem.id, type: inventoryItem.type }),
            description: inventoryItem.name || '',
            quantity: 1,
            unitPrice: inventoryItem.purchasePrice || inventoryItem.salesPrice || 0
          });
        }
      });
      
      logger.log(`Auto-populated ${this.productRows.length} product/service rows for selected vendor`);
    },
    getAvailableItemsForRow(currentRowIndex) {
      // Add safety check for productRows array
      if (!this.productRows || !Array.isArray(this.productRows)) {
        return this.productServiceList || [];
      }
      
      // Add safety check for productServiceList
      if (!this.productServiceList || !Array.isArray(this.productServiceList)) {
        return [];
      }
      
      // Get items that are not already selected in other rows
      const selectedIdsInOtherRows = this.productRows
        .map((row, idx) => {
          if (idx === currentRowIndex) return null; // Skip current row
          if (row && row.itemId && row.itemType && row.selectedItem) {
            try {
              const parsed = JSON.parse(row.selectedItem);
              return `${parsed.type}-${parsed.id}`;
            } catch (e) {
              return null;
            }
          }
          return null;
        })
        .filter(Boolean);
      
      // Return items that are not already selected in other rows
      return this.productServiceList.filter(item => {
        if (!item || !item.type || !item.id) return false;
        const itemKey = `${item.type}-${item.id}`;
        return !selectedIdsInOtherRows.includes(itemKey);
      });
    },
    removeAccountRow(idx) {
      this.accountRows.splice(idx, 1);
    },
    addProductRow() {
      this.productRows.push({ itemId: '', itemType: '', selectedItem: '', description: '', quantity: 0, unitPrice: 0 });
    },
    removeProductRow(idx) {
      this.productRows.splice(idx, 1);
      // Item becomes available again in other dropdowns - reactivity will handle this
    },
    onProductServiceChange(row) {
      if (typeof row.selectedItem === 'string') {
        try {
          const parsed = JSON.parse(row.selectedItem);
          row.itemId = parsed.id;
          row.itemType = parsed.type;
        } catch (e) {
          row.itemId = '';
          row.itemType = '';
        }
      }
      const item = this.productServiceList.find(i => i.id === row.itemId && i.type === row.itemType);
      if (item) {
        // Use purchasePrice when available and vendor is selected, otherwise use salesPrice
        const priceToUse = (this.vendorId && item.purchasePrice && item.purchasePrice > 0) 
          ? item.purchasePrice 
          : item.salesPrice;
        row.unitPrice = Number(priceToUse) || 0;
      } else {
        row.unitPrice = 0;
      }
    },
    updateDueDate() {
      const selectedTerm = this.paymentTermsList.find(term => String(term.id) === String(this.paymentTerm));
      if (selectedTerm && this.billDate) {
        const dueDays = Number(selectedTerm.dueDays) || 0;
        const billDateObj = this.parseDateFromDatePicker(this.billDate);
        billDateObj.setDate(billDateObj.getDate() + dueDays);
        this.dueDate = this.formatDateForDatePicker(billDateObj);
      }
    },
    formatDateForDatePicker(date) {
      if (!date) return '';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    },
    parseDateFromDatePicker(dateString) {
      if (!dateString || !dateString.includes('/')) return new Date();
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const month = parseInt(parts[0], 10) - 1;
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
      }
      return new Date();
    },
    async generateInvNo() {
      const vendor = this.selectedVendor ? this.selectedVendor.vendorName || this.selectedVendor.name || '' : '';
      let dueDate = this.dueDate || '';
      if (dueDate) {
        const parsedDate = this.parseDateFromDatePicker(dueDate);
        const yyyy = parsedDate.getFullYear();
        const mm = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const dd = String(parsedDate.getDate()).padStart(2, '0');
        dueDate = `${yyyy}${mm}${dd}`;
      }
      
      // Get the next serial number based on vendor's previous bills
      const nextSerial = await this.getNextSerialNumber();
      const serial = String(nextSerial).padStart(3, '0');
      
      let parts = [];
      if (vendor) parts.push(vendor);
      parts.push('BILL');
      if (dueDate) parts.push(dueDate);
      parts.push(serial);
      this.invNo = parts.join('-');
    },
    async getNextSerialNumber() {
      if (!this.vendorId || this.vendorBills.length === 0) {
        return this.serialNo;
      }
      
      // Extract serial numbers from existing bill numbers for this vendor
      const vendorName = this.selectedVendor ? this.selectedVendor.vendorName || this.selectedVendor.name || '' : '';
      const billNumbers = this.vendorBills
        .map(bill => bill.billNo || bill.invNo || '')
        .filter(billNo => billNo.includes(vendorName) && billNo.includes('BILL'))
        .map(billNo => {
          // Extract serial number from bill number format: VENDOR-BILL-N{days}-{date}-{serial}
          const parts = billNo.split('-');
          const lastPart = parts[parts.length - 1];
          return parseInt(lastPart, 10) || 0;
        })
        .filter(serial => !isNaN(serial));
      
      if (billNumbers.length === 0) {
        return this.serialNo;
      }
      
      // Find the highest serial number and increment it
      const maxSerial = Math.max(...billNumbers);
      return maxSerial + 1;
    },
    async validatePayFromBalance() {
      this.payFromBalanceError = '';
      if (!this.payFrom) return;
      try {
        const res = await ApiService.get(`/company-account/${this.payFrom}`);
        const account = res.data.account || res.data;
        const total = this.accountTotal + this.productServiceTotal;
        if (account && Number(account.currentBalance) < total) {
          this.payFromBalanceError = 'Insufficient balance in the selected account.';
        }
      } catch (e) {
        this.payFromBalanceError = 'Unable to check account balance.';
      }
    },
    async handleSaveAndClose() {
      // Validate required fields first
      const validationErrors = this.validateRequiredFields();
      if (validationErrors.length > 0) {
        this.showValidationError(validationErrors);
        return;
      }

      await this.validatePayFromBalance();
      if (this.payFromBalanceError) return;

      this.beginEnterBillPipeline({
        pendingAction: 'saveAndCloseToPayBill',
        savedInvNo: this.invNo,
        journalEntry: null,
        journalSkipped: false,
        journalFailed: false,
        journalFailureDetail: ''
      });
      this.updateEnterBillProgressorStep('bill', { status: 'active' });

      try {
        await this.saveBill();
        await this.runCompanyBillNotificationsAfterSave();
        this.updateEnterBillProgressorStep('bill', { status: 'done', detail: 'Saved to server.' });
        this.updateEnterBillProgressorStep('journal', { status: 'active' });
        this.journalAutoCreateActive = true;
      } catch (err) {
        this.abortEnterBillPipeline();
        this.showBillSaveAlert(err);
      }
    },
    async handleSaveAndNew() {
      // Validate required fields first
      const validationErrors = this.validateRequiredFields();
      if (validationErrors.length > 0) {
        this.showValidationError(validationErrors);
        return;
      }

      await this.validatePayFromBalance();
      if (this.payFromBalanceError) return;

      this.beginEnterBillPipeline({
        pendingAction: 'saveAndNew',
        savedInvNo: this.invNo,
        journalEntry: null,
        journalSkipped: false,
        journalFailed: false,
        journalFailureDetail: ''
      });
      this.updateEnterBillProgressorStep('bill', { status: 'active' });

      try {
        await this.saveBill();
        await this.runCompanyBillNotificationsAfterSave();
        this.updateEnterBillProgressorStep('bill', { status: 'done', detail: 'Saved to server.' });
        this.updateEnterBillProgressorStep('journal', { status: 'active' });
        this.journalAutoCreateActive = true;
      } catch (err) {
        this.abortEnterBillPipeline();
        this.showBillSaveAlert(err);
      }
    },
    async fetchConsultantsForVendor(vendorId) {
      if (!this.isConsultantIndustry) {
        this.consultantTimesheets = [];
        this.consultantOptions = [];
        this.allConsultantOptions = [];
        return;
      }
      if (!vendorId) {
        this.consultantTimesheets = [];
        this.consultantOptions = [];
        this.allConsultantOptions = [];
        // Always keep consultant section visible
        return;
      }
      
      this.consultantsLoading = true;
      this.consultantsError = '';
      
      try {
        const companyId = this.$route.params.id;
        let consultantOptionsList = [];

        // Hide timesheets already on Draft bills or vendor-portal submissions
        let draftLinkedSet = new Set();
        try {
          const params = new URLSearchParams({ vendorId: String(vendorId) });
          const draftRes = await ApiService.get(
            `/vendor/enter-bill/company/${companyId}/draft-timesheet-ids?${params.toString()}`
          );
          const ids = Array.isArray(draftRes.data?.timesheetIds) ? draftRes.data.timesheetIds : [];
          draftLinkedSet = new Set(ids.map((id) => String(id)));
        } catch (draftErr) {
          logger.warn('Could not load draft-linked timesheet IDs for Enter Bill:', draftErr);
        }

        const isTimesheetSelectableForNewBill = (ts) => {
          const isSpecificTimesheet =
            this.filterTimesheetId && String(ts.id) === String(this.filterTimesheetId);
          if (isSpecificTimesheet) return true;
          if (ts.billStatus !== 'Not Applicable' && ts.billStatus) return false;
          if (ts.isVendorInvoice === true || ts.isVendorInvoice === 'true') return false;
          if (draftLinkedSet.has(String(ts.id))) return false;
          return true;
        };
        
        // Step 1: Fetch all consultants for the company to separate by can_work_multiple_customers
        let allConsultants = [];
        try {
          const allConsultantsRes = await ApiService.getAllConsultants(companyId);
          allConsultants = Array.isArray(allConsultantsRes) ? allConsultantsRes : [];
          logger.log('Fetched all consultants:', allConsultants.length);
        } catch (err) {
          logger.error('Error fetching all consultants:', err);
          allConsultants = [];
        }
        
        // Step 2: Separate consultants into two groups based on can_work_multiple_customers
        const multiCustomerConsultants = allConsultants.filter(c => c.can_work_multiple_customers === true);
        const singleCustomerConsultants = allConsultants.filter(c => !c.can_work_multiple_customers || c.can_work_multiple_customers === false);
        
        logger.log('Multi-customer consultants:', multiCustomerConsultants.length);
        logger.log('Single-customer consultants:', singleCustomerConsultants.length);
        
        // Step 3: Handle multi-customer consultants (can_work_multiple_customers = true)
        // Fetch ConsultantCustomerService records where preferredVendorId matches selected vendor
        if (multiCustomerConsultants.length > 0) {
          try {
            const companyId = this.$route.params.id;
            const serviceUrl = companyId 
              ? `/customer/consultant-customer-services?companyId=${companyId}&preferredVendorId=${vendorId}`
              : `/customer/consultant-customer-services?preferredVendorId=${vendorId}`;
            
            logger.log('Fetching ConsultantCustomerService records for vendor:', vendorId);
            const serviceResponse = await ApiService.get(serviceUrl);
            const services = Array.isArray(serviceResponse.data) ? serviceResponse.data : [];
            
            logger.log('Found ConsultantCustomerService records:', services.length);
            
            // Get unique consultant IDs from services
            const consultantIdsFromServices = [...new Set(services.map(s => String(s.consultantId)))];
            logger.log('Consultant IDs from ConsultantCustomerService:', consultantIdsFromServices);
            
            // Filter multi-customer consultants that have services for this vendor
            const relevantMultiCustomerConsultants = multiCustomerConsultants.filter(c => 
              consultantIdsFromServices.includes(String(c.id))
            );
            
            logger.log('Relevant multi-customer consultants:', relevantMultiCustomerConsultants.length);
            
            // For each relevant consultant, fetch timesheets
            for (const consultant of relevantMultiCustomerConsultants) {
              try {
                // Get services for this consultant that match the vendor
                const consultantServices = services.filter(s => String(s.consultantId) === String(consultant.id));
                const serviceIds = consultantServices.map(s => s.id);
                
                logger.log(`Consultant ${consultant.id} has ${consultantServices.length} services for vendor ${vendorId}`);
                
                // Fetch timesheets for this consultant
                const timesheetsRes = await ApiService.get(`/consultant/timesheet?companyId=${companyId}&consultantId=${consultant.id}`);
                const allTimesheets = Array.isArray(timesheetsRes.data?.data) ? timesheetsRes.data.data : 
                                     Array.isArray(timesheetsRes.data) ? timesheetsRes.data : [];
                
                // Filter timesheets where:
                // 1. consultantCustomerServiceId matches one of the service IDs
                // 2. billStatus = 'Not Applicable'
                // 3. not vendor-portal / not already on a draft bill
                const relevantTimesheets = allTimesheets.filter(ts => {
                  const hasMatchingService = ts.consultantCustomerServiceId && 
                    serviceIds.some(serviceId => String(ts.consultantCustomerServiceId) === String(serviceId));
                  const isAvailable = isTimesheetSelectableForNewBill(ts);
                  
                  // Also include specific timesheet if filterTimesheetId is provided
                  const isSpecificTimesheet = this.filterTimesheetId && String(ts.id) === String(this.filterTimesheetId);
                  
                  return (hasMatchingService && isAvailable) || isSpecificTimesheet;
                });
                
                logger.log(`Found ${relevantTimesheets.length} relevant timesheets for consultant ${consultant.id}`);
                
                // Process each timesheet
                for (const timesheet of relevantTimesheets) {
                  // Find the matching ConsultantCustomerService for this timesheet
                  const matchingService = consultantServices.find(s => 
                    String(s.id) === String(timesheet.consultantCustomerServiceId)
                  );
                  
                  if (matchingService || (this.filterTimesheetId && String(timesheet.id) === String(this.filterTimesheetId))) {
                    // Use purchasePrice, expenseAccountId, and paymentTermId from ConsultantCustomerService
                    const hourlyRate = Number(matchingService?.purchasePrice) || Number(consultant.purchasePrice) || 0;
                    const expenseAccountId = matchingService?.expenseAccountId || null;
                    const paymentTermId = matchingService?.paymentTermId || null;
                    
                    const option = {
                      id: consultant.id,
                      name: consultant.consultantName || consultant.name || '',
                      description: `${timesheet.fromDate} to ${timesheet.toDate}`,
                      hourlyRate: hourlyRate,
                      hours: Number(timesheet.hours) || 0,
                      timesheetId: timesheet.id,
                      attachment: timesheet.attachment || null,
                      attachments: timesheet.attachments || [],
                      can_work_multiple_customers: true,
                      expenseAccountId: expenseAccountId,
                      paymentTermId: paymentTermId,
                      consultantCustomerServiceId: timesheet.consultantCustomerServiceId || matchingService?.id || null
                    };
                    consultantOptionsList.push(option);
                  }
                }
              } catch (err) {
                logger.error(`Error processing multi-customer consultant ${consultant.id}:`, err);
              }
            }
          } catch (err) {
            logger.error('Error fetching ConsultantCustomerService records:', err);
          }
        }
        
        // Step 4: Handle single-customer consultants (can_work_multiple_customers = false)
        // Filter consultants where preferredVendorId matches selected vendor
        const relevantSingleCustomerConsultants = singleCustomerConsultants.filter(c => 
          c.preferredVendorId && String(c.preferredVendorId) === String(vendorId) && c.buyService === true
        );
        
        logger.log('Relevant single-customer consultants:', relevantSingleCustomerConsultants.length);
        
        // For each relevant consultant, fetch timesheets
        for (const consultant of relevantSingleCustomerConsultants) {
          try {
            // Fetch timesheets for this consultant
            const timesheetsRes = await ApiService.get(`/consultant/timesheet?companyId=${companyId}&consultantId=${consultant.id}`);
            const allTimesheets = Array.isArray(timesheetsRes.data?.data) ? timesheetsRes.data.data : 
                                 Array.isArray(timesheetsRes.data) ? timesheetsRes.data : [];
            
            // Filter timesheets where:
            // 1. consultantId matches
            // 2. billStatus = 'Not Applicable'
            // 3. not vendor-portal / not already on a draft bill
            const relevantTimesheets = allTimesheets.filter(ts => {
              const matchesConsultant = String(ts.consultantId) === String(consultant.id);
              const isAvailable = isTimesheetSelectableForNewBill(ts);
              
              // Also include specific timesheet if filterTimesheetId is provided
              const isSpecificTimesheet = this.filterTimesheetId && String(ts.id) === String(this.filterTimesheetId);
              
              return (matchesConsultant && isAvailable) || isSpecificTimesheet;
            });
            
            logger.log(`Found ${relevantTimesheets.length} relevant timesheets for consultant ${consultant.id}`);
            
            // Process each timesheet
            for (const timesheet of relevantTimesheets) {
              // Use purchasePrice, expenseAccountId from Consultant model
              const hourlyRate = Number(consultant.purchasePrice) || 0;
              const expenseAccountId = consultant.expenseAccountId || null;
              const paymentTermId = consultant.paymentTermId || null;
              
              const option = {
                id: consultant.id,
                name: consultant.consultantName || consultant.name || '',
                description: `${timesheet.fromDate} to ${timesheet.toDate}`,
                hourlyRate: hourlyRate,
                hours: Number(timesheet.hours) || 0,
                timesheetId: timesheet.id,
                attachment: timesheet.attachment || null,
                attachments: timesheet.attachments || [],
                can_work_multiple_customers: false,
                expenseAccountId: expenseAccountId,
                paymentTermId: paymentTermId,
                consultantCustomerServiceId: null
              };
              consultantOptionsList.push(option);
            }
          } catch (err) {
            logger.error(`Error processing single-customer consultant ${consultant.id}:`, err);
          }
        }
        
        // Step 5: Apply additional filters if provided
        // Filter by specific consultant if filterConsultantId is set
        if (this.filterConsultantId) {
          consultantOptionsList = consultantOptionsList.filter(option => 
            String(option.id) === String(this.filterConsultantId)
          );
          logger.log(`Filtered to consultant ID ${this.filterConsultantId}, found ${consultantOptionsList.length} timesheet(s)`);
        }
        
        // Filter by specific timesheet if timesheetId is provided
        if (this.filterTimesheetId) {
          consultantOptionsList = consultantOptionsList.filter(option => 
            String(option.timesheetId) === String(this.filterTimesheetId)
          );
          logger.log(`Filtered to timesheet ID ${this.filterTimesheetId}, found ${consultantOptionsList.length} timesheet(s)`);
        }
        
        // Filter by consultantCustomerServiceId if provided
        if (this.consultantCustomerServiceId) {
          consultantOptionsList = consultantOptionsList.filter(option => 
            option.consultantCustomerServiceId && 
            String(option.consultantCustomerServiceId) === String(this.consultantCustomerServiceId)
          );
          logger.log(`Filtered to consultantCustomerServiceId ${this.consultantCustomerServiceId}, found ${consultantOptionsList.length} timesheet(s)`);
        }
        
        // Store all options
        this.allConsultantOptions = [...consultantOptionsList];
        this.consultantOptions = consultantOptionsList;
        
        if (consultantOptionsList.length === 0) {
          // Always keep consultant section visible
          this.consultantTimesheets = [];
          this.consultantOptions = [];
          this.allConsultantOptions = [];
          if (this.filterConsultantId || this.filterTimesheetId) {
            this.showSuccessModal('Info', `This consultant/timesheet is not associated with the selected vendor, or has no available timesheets.`);
          } else {
            this.showSuccessModal('Info', 'This vendor has no preferred consultants with available timesheets.');
          }
          return;
        }
        
        this.showConsultantTimesheets = true;
        
        // Automatically add consultant timesheets to the bill
        if (consultantOptionsList.length > 0) {
          // Clear any existing consultant entries first
          this.consultantTimesheets = [];
          
          // Auto-add consultant timesheets
          consultantOptionsList.forEach(option => {
            this.consultantTimesheets.push({
              id: option.id,
              name: option.name,
              description: option.description,
              hours: Number(option.hours) || 0,
              hourlyRate: Number(option.hourlyRate) || 0,
              timesheetId: option.timesheetId,
              attachment: option.attachment || null,
              attachments: option.attachments || [],
              expenseAccountId: option.expenseAccountId || null,
              paymentTermId: option.paymentTermId || null,
              consultantCustomerServiceId: option.consultantCustomerServiceId || null
            });
          });
          
          // If paymentTermId is available from ConsultantCustomerService, use it instead of vendor's default
          const paymentTermIds = consultantOptionsList
            .map(opt => opt.paymentTermId)
            .filter(id => id != null);
          
          if (paymentTermIds.length > 0) {
            const ccsPaymentTermId = paymentTermIds[0];
            const matchingPaymentTerm = this.paymentTermsList.find(term => 
              String(term.id) === String(ccsPaymentTermId)
            );
            if (matchingPaymentTerm) {
              this.paymentTerm = ccsPaymentTermId;
              this.previousPaymentTerm = ccsPaymentTermId;
              logger.log('Using paymentTermId from ConsultantCustomerService:', ccsPaymentTermId);
            }
          }
          
          if (this.filterTimesheetId) {
            this.showSuccessModal('Success', `Added selected timesheet to the bill`);
          } else {
            this.showSuccessModal('Success', `Automatically added ${consultantOptionsList.length} consultant timesheet entries to the bill`);
          }
        } else {
          if (this.filterTimesheetId) {
            this.showSuccessModal('Info', 'The selected timesheet is not available for this vendor or has already been added to a bill.');
          } else {
            this.showSuccessModal('Info', 'No available consultant timesheet entries, vendor product items, or vendor service items found for this vendor. All items may have already been invoiced or paid.');
          }
        }
        
        // Note: Expense accounts are now fetched independently when vendor is selected
        
      } catch (err) {
        this.consultantsError = err.response?.data?.error || err.message || 'Failed to fetch consultant timesheets';
        this.consultantTimesheets = [];
        this.consultantOptions = [];
        this.allConsultantOptions = [];
        // Always keep consultant section visible
        this.showAlertModal('Could not load consultants', this.consultantsError);
      } finally {
        this.consultantsLoading = false;
      }
    },
    async fetchExpenseAccounts(vendorId) {
      try {
        const companyId = this.$route.params.id;
        let allAccountMappings = [];

        // Fetch consultant expense accounts from the new API
        // BUT: For multi-customer consultants, use expenseAccountId from ConsultantCustomerService (stored in consultantTimesheets)
        try {
          const accountsRes = await ApiService.get(`/consultant/consultants-expense-accounts/${vendorId}`);
          const consultantsWithExpenseAccounts = Array.isArray(accountsRes.data.data) ? accountsRes.data.data : [];
          logger.log(`Consultant expense accounts for vendor ${vendorId}:`, consultantsWithExpenseAccounts);
          
          // Add consultant mappings
          // For multi-customer consultants, prioritize expenseAccountId from ConsultantCustomerService
          this.consultantTimesheets.forEach(consultant => {
            // Check if this consultant has expenseAccountId from ConsultantCustomerService
            let expenseAccountIdToUse = null;
            
            if (consultant.expenseAccountId) {
              // Use expenseAccountId from ConsultantCustomerService (for multi-customer consultants)
              expenseAccountIdToUse = consultant.expenseAccountId;
              logger.log(`Using expenseAccountId from ConsultantCustomerService for consultant ${consultant.id}:`, expenseAccountIdToUse);
            } else {
              // Fallback: find expenseAccountId from the API response (for single-customer consultants)
              const consultantFromApi = consultantsWithExpenseAccounts.find(c => 
                String(c.consultantId) === String(consultant.id)
              );
              if (consultantFromApi && consultantFromApi.expenseAccountId) {
                expenseAccountIdToUse = consultantFromApi.expenseAccountId;
              }
            }
            
            if (expenseAccountIdToUse) {
              allAccountMappings.push({
                type: 'consultant',
                accountId: expenseAccountIdToUse,
                description: `Consultant Expense - ${consultant.name}`,
                itemName: consultant.name,
                consultantId: consultant.id,
                timesheetId: consultant.timesheetId // Store timesheetId for reference
              });
            }
          });
          
          // Also add mappings for consultants not yet in consultantTimesheets (for dropdown options)
          consultantsWithExpenseAccounts.forEach(consultant => {
            // Only add if not already added from consultantTimesheets
            const alreadyAdded = allAccountMappings.some(m => 
              m.type === 'consultant' && 
              String(m.consultantId) === String(consultant.consultantId) &&
              String(m.accountId) === String(consultant.expenseAccountId)
            );
            
            if (!alreadyAdded && consultant.expenseAccountId) {
              allAccountMappings.push({
                type: 'consultant',
                accountId: consultant.expenseAccountId,
                description: `Consultant Expense - ${consultant.consultantName}`,
                itemName: consultant.consultantName,
                consultantId: consultant.consultantId
              });
            }
          });
        } catch (err) {
          logger.error('Error fetching consultant expense accounts:', err);
        }

        // Fetch product items expense accounts from the new API
        try {
          const productRes = await ApiService.get(`/vendor/product-items/product-items-expense-accounts/${vendorId}?companyId=${companyId}`);
          const productItemsWithExpenseAccounts = Array.isArray(productRes.data.data) ? productRes.data.data : [];
          
          logger.log(`Product items with expense accounts for vendor ${vendorId}:`, productItemsWithExpenseAccounts);
          
          // Add product expense account mappings
          productItemsWithExpenseAccounts.forEach(productItem => {
            if (productItem.expenseAccountId && productItem.expenseAccount) {
              allAccountMappings.push({
                type: 'product',
                accountId: productItem.expenseAccountId,
                description: `Product Expense - ${productItem.productName}`,
                itemName: productItem.productName,
                accountNumber: productItem.expenseAccount.accountNumber,
                accountType: productItem.expenseAccount.accountType,
                productItemId: productItem.productItemId,
                purchasePrice: productItem.purchasePrice,
                purchaseDescription: productItem.purchaseDescription
              });
            }
          });
        } catch (err) {
          logger.error('Error fetching product items expense accounts:', err);
        }

        // Fetch service items expense accounts from the new API
        try {
          const serviceRes = await ApiService.get(`/vendor/service-items/service-items-expense-accounts/${vendorId}?companyId=${companyId}`);
          const serviceItemsWithExpenseAccounts = Array.isArray(serviceRes.data.data) ? serviceRes.data.data : [];
          
          logger.log(`Service items with expense accounts for vendor ${vendorId}:`, serviceItemsWithExpenseAccounts);
          
          // Add service expense account mappings
          serviceItemsWithExpenseAccounts.forEach(serviceItem => {
            if (serviceItem.expenseAccountId && serviceItem.expenseAccount) {
              allAccountMappings.push({
                type: 'service',
                accountId: serviceItem.expenseAccountId,
                description: `Service Expense - ${serviceItem.serviceName}`,
                itemName: serviceItem.serviceName,
                accountNumber: serviceItem.expenseAccount.accountNumber,
                accountType: serviceItem.expenseAccount.accountType,
                serviceItemId: serviceItem.serviceItemId,
                purchasePrice: serviceItem.purchasePrice,
                purchaseDescription: serviceItem.purchaseDescription
              });
            }
          });
        } catch (err) {
          logger.error('Error fetching service items expense accounts:', err);
        }
        
        logger.log('All account mappings:', allAccountMappings);
        
        // Always call autoPopulateAccountRows to handle both cases: with data and without data
        this.autoPopulateAccountRows(allAccountMappings);
      } catch (err) {
        logger.error('Error fetching expense accounts:', err);
        // Don't show error toast as this is supplementary data
      }
    },
    // Calculate total amount for consultant timesheets by expense account ID
    getConsultantTotalByAccountId(accountId) {
      return this.consultantTimesheets.reduce((total, consultant) => {
        // For multi-customer consultants, use expenseAccountId from ConsultantCustomerService
        // Otherwise, find the consultant's expense account ID from the mappings
        let consultantAccountId = null;
        
        if (consultant.expenseAccountId) {
          // Use expenseAccountId from ConsultantCustomerService (for multi-customer consultants)
          consultantAccountId = consultant.expenseAccountId;
        } else {
          // Fallback: Find the consultant's expense account ID from the mappings
          const consultantMapping = this.expenseAccountMappings.find(m => 
            m.type === 'consultant' && m.consultantId === consultant.id
          );
          
          if (consultantMapping) {
            consultantAccountId = consultantMapping.accountId;
          }
        }
        
        // Only add to total if the account ID matches
        if (consultantAccountId && String(consultantAccountId) === String(accountId)) {
          const hours = Number(consultant.hours) || 0;
          const hourlyRate = Number(consultant.hourlyRate) || 0;
          return total + (hours * hourlyRate);
        }
        return total;
      }, 0);
    },
    // Calculate total amount for product/service items by expense account ID
    getProductServiceTotalByAccountId(accountId) {
      return this.productRows.reduce((total, row) => {
        // Find the product/service's expense account ID from the mappings
        const productServiceMapping = this.expenseAccountMappings.find(m => 
          (m.type === 'product' || m.type === 'service') && 
          m.accountId === accountId && 
          ((m.type === 'product' && m.productItemId === row.itemId) || 
           (m.type === 'service' && m.serviceItemId === row.itemId))
        );
        
        if (productServiceMapping && row.quantity && row.unitPrice) {
          return total + ((Number(row.quantity) || 0) * (Number(row.unitPrice) || 0));
        }
        return total;
      }, 0);
    },
    // Update account row amounts based on calculated totals
    updateAccountRowAmounts() {
      this.accountRows.forEach(row => {
        if (row.accountId) {
          const consultantTotal = this.getConsultantTotalByAccountId(row.accountId);
          const productServiceTotal = this.getProductServiceTotalByAccountId(row.accountId);
          row.amount = consultantTotal + productServiceTotal;
        }
      });
    },
    // Get detailed breakdown for a specific account - showing individual records from Product/Service section
    getAccountBreakdown(accountId) {
      const breakdown = [];
      
      // Add consultant timesheet breakdowns (these are separate from product/service items)
      this.consultantTimesheets.forEach(consultant => {
        // For multi-customer consultants, use expenseAccountId from ConsultantCustomerService
        // Otherwise, find the consultant's expense account ID from the mappings
        let consultantAccountId = null;
        
        if (consultant.expenseAccountId) {
          // Use expenseAccountId from ConsultantCustomerService (for multi-customer consultants)
          consultantAccountId = consultant.expenseAccountId;
        } else {
          // Fallback: Find the consultant's expense account ID from the mappings
          const consultantMapping = this.expenseAccountMappings.find(m => 
            m.type === 'consultant' && m.consultantId === consultant.id
          );
          
          if (consultantMapping) {
            consultantAccountId = consultantMapping.accountId;
          }
        }
        
        // Only add to breakdown if the account ID matches and timesheet exists
        if (consultantAccountId && String(consultantAccountId) === String(accountId) && consultant.timesheetId) {
          const hours = Number(consultant.hours) || 0;
          const hourlyRate = Number(consultant.hourlyRate) || 0;
          const amount = hours * hourlyRate;
          
          if (amount > 0) {
            breakdown.push({
              type: 'consultant',
              id: consultant.timesheetId,
              name: consultant.name,
              description: consultant.description,
              hours: hours,
              hourlyRate: hourlyRate,
              amount: amount,
              source: 'Consultant Timesheet'
            });
          }
        }
      });
      
      // Add individual product/service item records that correspond to this account
      this.productRows.forEach((row, rowIndex) => {
        if (row.itemId && row.itemType && row.quantity && row.unitPrice) {
          const productServiceMapping = this.expenseAccountMappings.find(m => 
            (m.type === 'product' || m.type === 'service') && 
            m.accountId === accountId && 
            ((m.type === 'product' && m.productItemId === row.itemId) || 
             (m.type === 'service' && m.serviceItemId === row.itemId))
          );
          
          if (productServiceMapping) {
            const quantity = Number(row.quantity) || 0;
            const unitPrice = Number(row.unitPrice) || 0;
            const amount = quantity * unitPrice;
            
            if (amount > 0) {
              // Get the source item details
              const sourceItem = this.productServiceList.find(i => i.id === row.itemId && i.type === row.itemType);
              
              breakdown.push({
                type: row.itemType,
                id: row.itemId,
                name: row.description || sourceItem?.name || 'Unnamed Item',
                description: `${row.itemType} Item`,
                quantity: quantity,
                unitPrice: unitPrice,
                amount: amount,
                source: `Product/Service Row ${rowIndex + 1}`,
                rowIndex: rowIndex,
                sourceItem: sourceItem
              });
            }
          }
        }
      });
      
      return breakdown;
    },
    // Get source information for a specific item
    getItemSourceInfo(itemId, itemType) {
      if (!itemId || !itemType) return '-';
      
      // Find the item in productServiceList
      const item = this.productServiceList.find(i => i.id === itemId && i.type === itemType);
      if (item) {
        return `${itemType} from vendor catalog`;
      }
      
      return 'Unknown source';
    },
    // Get detailed source records for a specific item
    getItemSourceRecords(itemId, itemType) {
      if (!itemId || !itemType) return [];
      
      const sourceRecords = [];
      
      // Find the item in productServiceList
      const item = this.productServiceList.find(i => i.id === itemId && i.type === itemType);
      if (item) {
        sourceRecords.push({
          id: item.id,
          type: `vendor-${itemType}`,
          name: item.name,
          description: item.description || item.name,
          quantity: 1, // Default quantity from catalog
          unitPrice: item.purchasePrice || item.salesPrice || 0,
          total: item.purchasePrice || item.salesPrice || 0,
          billStatus: 'Available', // This would come from the backend
          vendorId: item.preferredVendor
        });
      }
      
      return sourceRecords;
    },
    // Get CSS class for bill status
    getBillStatusClass(billStatus) {
      switch (billStatus) {
        case 'Available':
          return 'vendor_enter_bill-status-available';
        case 'Bill Created':
          return 'vendor_enter_bill-status-bill-created';
        case 'Paid':
          return 'vendor_enter_bill-status-paid';
        case 'Not Applicable':
          return 'vendor_enter_bill-status-not-applicable';
        default:
          return 'vendor_enter_bill-status-default';
      }
    },
    autoPopulateAccountRows(allAccountMappings) {
      logger.log('autoPopulateAccountRows called with:', allAccountMappings);
      // Store the mappings for amount calculations
      this.expenseAccountMappings = allAccountMappings;
      
      // Clear existing account rows
      this.accountRows = [];
      
      allAccountMappings.forEach(mapping => {
        // Add expense account if available
        if (mapping.accountId) {
          this.accountRows.push({
            accountId: mapping.accountId,
            description: mapping.description,
            amount: 0
          });
        }
      });
      
      // Only show feedback and add default row if there are actual mappings
      if (allAccountMappings.length > 0) {
        // Update amounts based on current data
        this.updateAccountRowAmounts();
        
        // Count by type for better user feedback
        const consultantCount = allAccountMappings.filter(m => m.type === 'consultant').length;
        const productCount = allAccountMappings.filter(m => m.type === 'product').length;
        const serviceCount = allAccountMappings.filter(m => m.type === 'service').length;
        
        let message = `Auto-populated ${this.accountRows.length} expense account records:`;
        if (consultantCount > 0) message += ` ${consultantCount} consultants,`;
        if (productCount > 0) message += ` ${productCount} products,`;
        if (serviceCount > 0) message += ` ${serviceCount} services,`;
        message = message.replace(/,$/, ''); // Remove trailing comma
        
        this.showSuccessModal('Info', message);
      } else {
        // No mappings found - clear account rows and show appropriate message
        this.accountRows = [];
        logger.log('No expense account mappings found for this vendor');
      }
    },
    addConsultantTimesheet() {
      // Get currently available options (not yet added to bill)
      const availableOptions = this.getAvailableConsultantOptions();
      
      if (availableOptions.length > 0) {
        // Add the first available timesheet record
        const availableOption = availableOptions[0];
        this.consultantTimesheets.push({
          id: availableOption.id,
          name: availableOption.name,
          description: availableOption.description,
          hours: Number(availableOption.hours) || 0,
          hourlyRate: Number(availableOption.hourlyRate) || 0,
          timesheetId: availableOption.timesheetId,
          attachment: availableOption.attachment || null,
          attachments: availableOption.attachments || [],
          expenseAccountId: availableOption.expenseAccountId || null, // Store expenseAccountId from ConsultantCustomerService
          paymentTermId: availableOption.paymentTermId || null, // Store paymentTermId from ConsultantCustomerService
          consultantCustomerServiceId: availableOption.consultantCustomerServiceId || null // Store for reference
        });
        
        // If paymentTermId is available from ConsultantCustomerService, use it
        if (availableOption.paymentTermId) {
          const matchingPaymentTerm = this.paymentTermsList.find(term => 
            String(term.id) === String(availableOption.paymentTermId)
          );
          if (matchingPaymentTerm && !this.paymentTerm) {
            // Only set if payment term is not already set
            this.paymentTerm = availableOption.paymentTermId;
            this.previousPaymentTerm = availableOption.paymentTermId;
            logger.log('Using paymentTermId from ConsultantCustomerService:', availableOption.paymentTermId);
          }
        }
        
        // Show success message
        this.showSuccessModal('Success', `Added consultant timesheet: ${availableOption.name} - ${availableOption.description}`);
      } else {
        // If no available timesheets, add empty row
        this.consultantTimesheets.push({ id: '', name: '', description: '', hours: 0, hourlyRate: 0 });
        this.showSuccessModal('Info', 'No more available consultant timesheets. You can manually enter consultant details.');
      }
    },
    getAvailableConsultantOptions() {
      // Get all timesheet IDs currently added to bill
      const addedTimesheetIds = this.consultantTimesheets
        .filter(c => c.timesheetId)
        .map(c => c.timesheetId);
      
      // Return options that are NOT currently added to the bill
      return this.consultantOptions.filter(option => 
        !addedTimesheetIds.includes(option.timesheetId)
      );
    },
    removeConsultantTimesheet(idx) {
      const removedItem = this.consultantTimesheets[idx];
      this.consultantTimesheets.splice(idx, 1);
      
      // Provide feedback if a timesheet was removed
      if (removedItem && removedItem.timesheetId) {
        this.showSuccessModal('Info', `Removed ${removedItem.name} timesheet from bill. It's now available to add again.`);
      }
    },
    onConsultantChange(idx) {
      const selectedName = this.consultantTimesheets[idx].name;
      const found = this.consultantOptions.find(opt => opt.name === selectedName);
      if (found) {
        this.consultantTimesheets[idx].description = found.description;
        this.consultantTimesheets[idx].hourlyRate = Number(found.hourlyRate) || 0;
        this.consultantTimesheets[idx].id = found.id;
        // Pre-fill hours with timesheet hours if available
        if (found.hours && Number(found.hours) > 0) {
          this.consultantTimesheets[idx].hours = Number(found.hours);
        }
        // Store timesheet ID if available
        if (found.timesheetId) {
          this.consultantTimesheets[idx].timesheetId = found.timesheetId;
        }
        if (found.attachment) {
          this.consultantTimesheets[idx].attachment = found.attachment;
        }
        if (found.attachments) {
          this.consultantTimesheets[idx].attachments = found.attachments;
        }
      } else {
        this.consultantTimesheets[idx].id = '';
        this.consultantTimesheets[idx].timesheetId = null;
        this.consultantTimesheets[idx].attachment = null;
        this.consultantTimesheets[idx].attachments = [];
      }
    },
    getConsultantTotal(consultant) {
      const hours = Number(consultant.hours) || 0;
      const hourlyRate = Number(consultant.hourlyRate) || 0;
      return (hours * hourlyRate).toFixed(2);
    },
    getAttachmentUrl(filename) {
      if (!filename) return '#';
      // Get companyId for nested directory structure
      const companyId = this.$route.params.id || this.$route.params.companyId;
      if (companyId) {
        return `${process.env.VUE_APP_API_URL}/consultant/attachment/${filename}?companyId=${companyId}`;
      }
      return `${process.env.VUE_APP_API_URL}/consultant/attachment/${filename}`;
    },
    // Bill-level attachments (reference: CustomerAddNewInvoice.vue)
    handleBillFileUpload(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;
      if (!this.compressingAttachments) this.attachmentCompressNote = '';
      const getMimeType = (filename, fallbackType) => {
        if (fallbackType && fallbackType !== '') return fallbackType;
        const extension = filename.split('.').pop().toLowerCase();
        const mimeMap = {
          'pdf': 'application/pdf',
          'doc': 'application/msword',
          'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'xls': 'application/vnd.ms-excel',
          'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'gif': 'image/gif',
          'txt': 'text/plain'
        };
        return mimeMap[extension] || 'application/octet-stream';
      };
      Array.from(files).forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const attachment = {
            fileName: file.name,
            fileSize: file.size,
            filePath: e.target.result,
            mimeType: getMimeType(file.name, file.type),
            uploadedAt: new Date().toISOString(),
            source: 'bill',
            file
          };
          if (!Array.isArray(this.attachments)) this.attachments = [];
          const attachmentIndex = this.attachments.length;
          this.attachments.push(attachment);
          this.$nextTick(() => {
            this.attachmentPreviewUrls[attachmentIndex] = URL.createObjectURL(file);
          });
        };
        reader.readAsDataURL(file);
      });
      if (this.$refs.addBillFileInput) {
        this.$refs.addBillFileInput.value = '';
      }
    },
    canCompressAttachment(attachment) {
      return isCompressibleAttachment(attachment);
    },
    attachmentFileKind(attachment) {
      const mime = String(attachment?.mimeType || '').toLowerCase();
      const ext = String(attachment?.fileName || '').split('.').pop().toLowerCase();
      if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
      if (mime.startsWith('image/')) return 'image';
      if (['doc', 'docx'].includes(ext)) return 'doc';
      if (['xls', 'xlsx', 'csv'].includes(ext)) return 'sheet';
      return 'file';
    },
    attachmentFileIcon(attachment) {
      const icons = {
        pdf: 'bi bi-file-earmark-pdf',
        image: 'bi bi-file-earmark-image',
        doc: 'bi bi-file-earmark-word',
        sheet: 'bi bi-file-earmark-excel',
        file: 'bi bi-file-earmark',
      };
      return icons[this.attachmentFileKind(attachment)];
    },
    compressResultLabel(attachment) {
      switch (attachment.compressionResult) {
        case 'done': return `Compressed from ${this.formatFileSize(attachment.originalFileSize)}`;
        case 'skipped': return 'Already small';
        default: return 'Could not compress';
      }
    },
    async compressBillAttachment(index) {
      const original = this.attachments[index];
      if (this.compressingAttachmentIndex !== null || !isCompressibleAttachment(original)) return null;
      this.compressingAttachmentIndex = index;
      if (!this.attachmentCompressQueue.length) this.attachmentCompressNote = '';
      try {
        const updated = await compressSingleAttachment(original);
        const currentIndex = this.attachments.indexOf(original);
        if (currentIndex === -1) return null;
        this.attachments.splice(currentIndex, 1, updated);
        if (updated.file && updated.file !== original.file) {
          if (this.attachmentPreviewUrls[currentIndex]) URL.revokeObjectURL(this.attachmentPreviewUrls[currentIndex]);
          this.attachmentPreviewUrls[currentIndex] = URL.createObjectURL(updated.file);
        }
        return updated;
      } finally {
        this.compressingAttachmentIndex = null;
      }
    },
    async compressBillAttachmentsForReview() {
      if (this.compressingAttachments) return;
      const targets = (this.attachments || []).filter(isCompressibleAttachment);
      if (!targets.length) return;
      this.attachmentCompressNote = '';
      this.$refs.billAttachmentsSection?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const beforeBytes = getAttachmentsTotalBytes(this.attachments);
      let compressedCount = 0;
      let failedCount = 0;
      this.attachmentCompressQueue = targets.slice();
      try {
        for (const target of targets) {
          this.attachmentCompressQueue = this.attachmentCompressQueue.filter((att) => att !== target);
          const index = this.attachments.indexOf(target);
          if (index === -1) continue;
          const updated = await this.compressBillAttachment(index);
          if (updated?.compressionResult === 'done') compressedCount += 1;
          if (updated?.compressionResult === 'failed') failedCount += 1;
        }
      } finally {
        this.attachmentCompressQueue = [];
      }
      this.attachmentCompressNote = describeAttachmentCompression(
        beforeBytes,
        getAttachmentsTotalBytes(this.attachments),
        { compressedCount, failedCount }
      );
      if (attachmentsExceedBillLimit(this.attachments)) {
        this.showAlertModal(
          'Unable to save bill',
          buildBillAttachmentLimitMessage(getAttachmentsTotalBytes(this.attachments))
        );
      }
    },
    removeBillAttachment(index) {
      if (this.compressingAttachments) return;
      this.attachmentCompressNote = '';
      if (this.attachments && this.attachments.length > index) {
        if (this.attachmentPreviewUrls[index]) {
          URL.revokeObjectURL(this.attachmentPreviewUrls[index]);
          delete this.attachmentPreviewUrls[index];
        }
        this.attachments.splice(index, 1);
        const newPreviewUrls = {};
        this.attachments.forEach((attachment, newIndex) => {
          if (attachment.file) {
            newPreviewUrls[newIndex] = URL.createObjectURL(attachment.file);
          }
        });
        this.attachmentPreviewUrls = newPreviewUrls;
      }
    },
    async viewBillAttachment(attachment) {
      if (!attachment || !attachment.filePath) return;
      try {
        const attachmentIndex = this.attachments.findIndex(a => a === attachment);
        const previewUrl = attachmentIndex >= 0 ? this.attachmentPreviewUrls[attachmentIndex] : null;
        if (previewUrl) {
          window.open(previewUrl, '_blank');
          return;
        }
        if (attachment.filePath.startsWith('data:')) {
          const base64Data = attachment.filePath.split(',')[1];
          const mimeType = attachment.mimeType || 'application/pdf';
          const blob = this.base64ToBlob(base64Data, mimeType);
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => window.URL.revokeObjectURL(url), 100);
          return;
        }
        const companyId = this.$route.params.id || this.$route.params.companyId;
        let filename = attachment.filePath.includes('/') ? attachment.filePath.split('/').pop() : attachment.filePath;
        const viewUrl = companyId
          ? `/vendor/documents/${filename}?companyId=${companyId}`
          : `/vendor/documents/${filename}`;
        const response = await ApiService.get(viewUrl, { responseType: 'blob' });
        const mimeType = response.headers['content-type'] || attachment.mimeType || 'application/pdf';
        const blob = new Blob([response.data], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } catch (error) {
        logger.error('Failed to view bill attachment:', error);
        alert('Failed to view attachment');
      }
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    isImageFile(attachment) {
      if (!attachment) return false;
      const mimeType = attachment.mimeType || '';
      return mimeType.startsWith('image/');
    },
    base64ToBlob(base64, mimeType) {
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: mimeType });
    },
    resolvedCompanyId() {
      const r = this.$route && this.$route.params;
      return (r && (r.companyId || r.id)) || null;
    },
    escapeHtmlForPdf(s) {
      return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },
    getBillNotifyPaymentTermLabel() {
      const term = (this.paymentTermsList || []).find((t) => String(t.id) === String(this.paymentTerm));
      if (!term) return '';
      return String(term.name || term.description || (term.dueDays != null ? `Net ${term.dueDays}` : '') || '').trim();
    },
    getBillNotifyAccountLinesForMessage() {
      return (this.visibleAccountRows || [])
        .filter((row) => row.accountId && row.amount && Number(row.amount) > 0)
        .map((row) => ({
          description: row.description || '',
          amount: Number(row.amount) || 0,
        }));
    },
    buildBillNotificationPdfHtml() {
      const companyName = this.escapeHtmlForPdf(
        this.$store.state.currentCompany?.name || this.$store.state.currentCompany?.companyName || ''
      );
      const vendorName = this.escapeHtmlForPdf(this.selectedVendor?.vendorName || '');
      const billNo = this.escapeHtmlForPdf(this.invNo || '');
      const billDate = this.escapeHtmlForPdf(this.convertDateForBackend(this.billDate) || '');
      const dueDate = this.escapeHtmlForPdf(this.convertDateForBackend(this.dueDate) || '');
      const deliveryDate = this.escapeHtmlForPdf(
        this.convertDateForBackend(this.deliveryDate || this.billDate) || ''
      );
      const comments = this.comments ? this.escapeHtmlForPdf(this.comments) : '';
      const rows = (this.visibleAccountRows || [])
        .filter((row) => row.accountId && row.amount && Number(row.amount) > 0)
        .map((row) => {
          const desc = this.escapeHtmlForPdf(row.description || '');
          const amt = Number(row.amount) || 0;
          return `<tr><td>${desc}</td><td class="num">$${amt.toFixed(2)}</td></tr>`;
        })
        .join('');
      const total = Number(this.finalTotal) || 0;
      return `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>
  body{font-family:Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#0f172a;margin:24px;}
  h1{font-size:22px;margin:0 0 4px 0;color:#312e81;}
  .meta{color:#64748b;font-size:13px;margin-bottom:20px;}
  table.hdr td{padding:6px 12px 6px 0;vertical-align:top;font-size:13px;}
  table.data{width:100%;border-collapse:collapse;margin-top:16px;font-size:13px;}
  table.data th,table.data td{border:1px solid #e2e8f0;padding:8px 10px;}
  table.data th{background:#eef2ff;text-align:left;font-size:12px;color:#4338ca;}
  table.data td.num{text-align:right;}
</style></head><body>
  <h1>Enter bill</h1>
  <div class="meta">${companyName}</div>
  <table class="hdr" cellpadding="0" cellspacing="0">
    <tr><td><strong>Vendor</strong></td><td>${vendorName}</td></tr>
    <tr><td><strong>Bill #</strong></td><td>${billNo}</td></tr>
    <tr><td><strong>Bill date</strong></td><td>${billDate}</td></tr>
    <tr><td><strong>Due date</strong></td><td>${dueDate}</td></tr>
    <tr><td><strong>Service date</strong></td><td>${deliveryDate}</td></tr>
    <tr><td><strong>Total</strong></td><td>$${total.toFixed(2)}</td></tr>
    ${comments ? `<tr><td><strong>Comments</strong></td><td>${comments}</td></tr>` : ''}
  </table>
  <table class="data">
    <thead><tr><th>Description</th><th class="num">Amount</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="2">No lines</td></tr>'}</tbody>
  </table>
</body></html>`;
    },
    buildBillCompanyNotifyPayload(companyId, pdfOverride = null) {
      const companyName =
        this.$store.state.currentCompany?.name ||
        this.$store.state.currentCompany?.companyName ||
        '';
      const paymentTermLabel = this.getBillNotifyPaymentTermLabel();
      const payload = {
        companyId,
        companyName,
        vendorName: this.selectedVendor?.vendorName || '',
        vendorEmail: this.selectedVendor?.email || '',
        billNo: this.invNo,
        billDate: this.convertDateForBackend(this.billDate),
        dueDate: this.convertDateForBackend(this.dueDate),
        deliveryDate: this.convertDateForBackend(this.deliveryDate || this.billDate),
        paymentTermLabel,
        total: this.finalTotal,
        contactName: this.contactName,
        phone: this.phone,
        comments: this.comments,
        accountLines: this.getBillNotifyAccountLinesForMessage(),
      };
      if (pdfOverride?.pdfBase64 && pdfOverride?.pdfFileName) {
        payload.pdfBase64 = pdfOverride.pdfBase64;
        payload.pdfFileName = pdfOverride.pdfFileName;
      }
      return payload;
    },
    async sendCompanyBillNotificationsAfterSave(companyId, billId, pdfOverride = null) {
      try {
        const cid = companyId || this.resolvedCompanyId();
        if (!cid || !billId) return;
        const payload = this.buildBillCompanyNotifyPayload(cid, pdfOverride);
        await notifyCompanyBillSaved(payload);
      } catch (e) {
        logger.warn('sendCompanyBillNotificationsAfterSave', e);
      }
    },
    async runCompanyBillNotificationsAfterSave() {
      const companyId = this.resolvedCompanyId();
      const billId = this.savedBillId;
      if (!companyId || !billId) return;
      let pdfAttachment = null;
      try {
        const htmlContent = this.buildBillNotificationPdfHtml();
        const pdfRes = await ApiService.post(`/vendor/enter-bill/${billId}/generate-notification-pdf`, {
          companyId,
          htmlContent,
        });
        const d = pdfRes.data?.data || pdfRes.data;
        if (d?.pdfBase64 && d?.pdfFileName) {
          pdfAttachment = { pdfBase64: d.pdfBase64, pdfFileName: d.pdfFileName };
        }
      } catch (pdfErr) {
        logger.warn('Bill notification PDF:', pdfErr);
      }
      await this.sendCompanyBillNotificationsAfterSave(companyId, billId, pdfAttachment);
    },
    async saveBill() {
      // Prepare payload according to backend structure
      // Each account record now represents an individual Product/Service item or Consultant timesheet
      // with its own individual amount (not grouped totals)
      // Service Date automatically uses Bill Date
      const deliveryDateToUse = this.deliveryDate || this.billDate;
      const payload = {
        vendorId: this.vendorId,
        contactName: this.contactName,
        phone: this.phone,
        paymentTerm: this.paymentTerm,
        date: this.convertDateForBackend(this.billDate),
        dueDate: this.convertDateForBackend(this.dueDate),
        deliveryDate: this.convertDateForBackend(deliveryDateToUse),
        billNo: this.invNo,
        total: this.finalTotal, // Include total amount
        comments: this.comments,
        attachments: (this.attachments || []).map(att => ({
          fileName: att.fileName,
          fileSize: att.fileSize,
          filePath: att.filePath,
          mimeType: att.mimeType,
          source: 'bill'
        })),
        accountRecords: this.visibleAccountRows
          .filter(row => row.accountId && row.amount && row.amount > 0)
          .map(row => ({
            accountId: row.accountId,
            description: row.description,
            amount: row.amount
          })),
        // Send product/service item records so backend can update their status to 'Bill Created'
        itemRecords: this.productRows
          .filter(row => row.itemId && row.itemType && row.quantity && row.unitPrice)
          .map(row => ({
            itemId: row.itemId,
            itemType: row.itemType,
            description: row.description,
            quantity: row.quantity,
            unitPrice: row.unitPrice
          })),
        // Send consultant timesheet records so backend can update their status to 'Bill Created'
        consultantTimesheetRecords: this.isConsultantIndustry
          ? this.consultantTimesheets
              .filter(consultant => consultant.timesheetId)
              .map(consultant => ({
                consultantId: consultant.id,
                timesheetId: consultant.timesheetId,
                hours: consultant.hours,
                hourlyRate: consultant.hourlyRate,
                description: consultant.description,
                attachment: consultant.attachment
              }))
          : []
      };
      
      // Debug: Log the payload being sent
      logger.log('💾 Saving bill with payload:');
      logger.log('  - Total Amount:', payload.total);
      logger.log('  - Account Total:', this.accountTotal);
      logger.log('  - Individual Account Records:', payload.accountRecords.length);
      logger.log('  - Item Records:', payload.itemRecords.length);
      logger.log('  - Consultant Records:', payload.consultantTimesheetRecords.length);
      logger.log('  - Individual Account Details:', payload.accountRecords);
      logger.log('  - Full Payload:', payload);
      
      const response = await ApiService.post('/vendor/enter-bill', payload);
      
      // Store the saved bill ID for journal entry reference
      if (response && response.data && response.data.id) {
        this.savedBillId = response.data.id;
        logger.log('✅ Bill saved successfully with ID:', response.data.id);
      }
      
      return response;
    },
    convertDateForBackend(dateString) {
      if (!dateString) return '';
      const date = this.parseDateFromDatePicker(dateString);
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },
    async resetForm(skipSuccessModal = false) {
      // Increment serial number for next bill
      this.serialNo++;
      
      // Reset all form fields
      this.vendorId = '';
      this.contactName = '';
      this.phone = '';
      this.paymentTerm = '';
      this.previousPaymentTerm = '';
      this.billDate = this.formatDateForDatePicker(new Date());
      this.dueDate = '';
      // Service Date automatically uses Bill Date
      this.deliveryDate = this.billDate;
      this.invNo = '';
      this.accountRows = [ { accountId: '', description: '', amount: 0 } ];
      this.productRows = [ { itemId: '', itemType: '', selectedItem: '', description: '', quantity: 0, unitPrice: 0 } ];
      this.comments = '';
      this.payFrom = '';
      this.payFromBalanceError = '';
      // Reset bill attachments
      this.attachments = [];
      Object.values(this.attachmentPreviewUrls || {}).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
      this.attachmentPreviewUrls = {};
      // Reset consultant timesheet data
      this.showConsultantTimesheets = false;
      this.consultantTimesheets = [];
      this.consultantOptions = [];
      this.allConsultantOptions = [];
      this.consultantsLoading = false;
      this.consultantsError = '';
      // Reset expense account mappings
      this.expenseAccountMappings = [];
      // Reset product service list
      this.productServiceList = [];
      // Reset vendor bills list
      this.vendorBills = [];
      // Reset saved bill ID
      this.savedBillId = null;
      this.activeTab = 'consultant';

      if (!skipSuccessModal) {
        this.showSuccessModal('Ready', 'Form reset successfully! Ready to create a new bill.', 'OK');
      }
    },
    handleCancel() {
      // Navigate back to the previous view
      this.$router.back();
    },
    createEnterBillProgressorSteps() {
      return [
        { key: 'bill', label: 'Save bill', status: 'pending', detail: '' },
        { key: 'journal', label: 'Create journal entries', status: 'pending', detail: '' }
      ];
    },
    beginEnterBillPipeline(pipeline) {
      this.enterBillPipeline = pipeline;
      this.enterBillProgressorSteps = this.createEnterBillProgressorSteps();
      this.enterBillProgressorFootMessage = '';
      this.enterBillProgressorFootTone = 'neutral';
      this.enterBillProgressorOkEnabled = false;
      this.enterBillProgressorShow = true;
    },
    abortEnterBillPipeline() {
      this.enterBillProgressorShow = false;
      this.enterBillProgressorOkEnabled = false;
      this.enterBillProgressorFootMessage = '';
      this.enterBillProgressorSteps = [];
      this.enterBillPipeline = null;
      this.journalAutoCreateActive = false;
    },
    updateEnterBillProgressorStep(key, patch) {
      const step = this.enterBillProgressorSteps.find((s) => s.key === key);
      if (step) {
        Object.assign(step, patch);
      }
    },
    setEnterBillPipelineComplete() {
      const p = this.enterBillPipeline;
      if (!p) return;
      if (p.journalFailed) {
        this.enterBillProgressorFootTone = 'warn';
        this.enterBillProgressorFootMessage =
          'Bill is saved; journal entries could not be posted. Click OK to continue.';
      } else if (p.journalSkipped) {
        this.enterBillProgressorFootTone = 'warn';
        this.enterBillProgressorFootMessage =
          'Bill is saved (journal was skipped). Click OK to continue.';
      } else {
        this.enterBillProgressorFootTone = 'success';
        this.enterBillProgressorFootMessage = 'All steps completed successfully. Click OK to finish.';
      }
      this.enterBillProgressorOkEnabled = true;
    },
    closeJournalEntryModal() {
      this.journalAutoCreateActive = false;
    },
    handleJournalEntrySaved(journalEntry) {
      if (this.enterBillPipeline) {
        this.journalAutoCreateActive = false;
        this.enterBillPipeline.journalEntry = journalEntry;
        const d = journalEntry?.entryNo ? `Entry number: ${journalEntry.entryNo}` : '';
        this.updateEnterBillProgressorStep('journal', { status: 'done', detail: d });
        this.setEnterBillPipelineComplete();
        return;
      }
      this.closeJournalEntryModal();
    },
    handleJournalEntryAutoSkipped({ reason } = {}) {
      const r = (reason || '').trim();
      if (this.enterBillPipeline) {
        this.journalAutoCreateActive = false;
        this.enterBillPipeline.journalEntry = null;
        this.enterBillPipeline.journalSkipped = true;
        this.enterBillPipeline.journalFailed = false;
        this.updateEnterBillProgressorStep('journal', {
          status: 'skipped',
          detail: r || 'Journal was not posted.'
        });
        this.setEnterBillPipelineComplete();
      }
    },
    handleJournalEntryAutoFailed({ error } = {}) {
      const r = (error || '').toString().trim();
      if (this.enterBillPipeline) {
        this.journalAutoCreateActive = false;
        this.enterBillPipeline.journalEntry = null;
        this.enterBillPipeline.journalSkipped = false;
        this.enterBillPipeline.journalFailed = true;
        this.enterBillPipeline.journalFailureDetail = r;
        this.updateEnterBillProgressorStep('journal', {
          status: 'error',
          detail: r || 'Could not create journal entries.'
        });
        this.setEnterBillPipelineComplete();
      }
    },
    enterBillJournalOutcomeLine(p) {
      if (p.journalFailed) {
        const d = (p.journalFailureDetail || '').trim();
        return d
          ? `Bill journal could not be created: ${d}`
          : 'Bill journal could not be created.';
      }
      if (p.journalSkipped) {
        return 'Bill journal was not created automatically.';
      }
      const entryNo = p.journalEntry?.entryNo || '';
      if (entryNo) {
        return `Bill journal created. Entry number: ${entryNo}.`;
      }
      return 'Bill journal created successfully.';
    },
    async onEnterBillProgressorOk() {
      if (!this.enterBillProgressorOkEnabled || !this.enterBillPipeline) return;
      const p = { ...this.enterBillPipeline };
      this.enterBillProgressorShow = false;
      this.enterBillProgressorOkEnabled = false;
      this.enterBillProgressorFootMessage = '';
      this.enterBillProgressorSteps = [];
      this.enterBillPipeline = null;

      await this.finalizeEnterBillPipelineOk(p);
    },
    async finalizeEnterBillPipelineOk(p) {
      const inv = p.savedInvNo || this.invNo;
      const journalLine = this.enterBillJournalOutcomeLine(p);

      if (p.pendingAction === 'saveAndNew') {
        await this.resetForm(true);
        this.showSuccessModal('Success', `Bill ${inv} saved. ${journalLine} Form reset for new bill.`, 'OK');
        return;
      }

      if (p.pendingAction === 'saveAndCloseToPayBill') {
        this.showSuccessModal('Success', `Bill ${inv} saved. ${journalLine}`, 'OK');
        this.$router.push(`/company/${this.$route.params.id}/dashboard/vendors/pay-bill`);
        return;
      }

      if (p.pendingAction === 'saveAndClose') {
        this.showSuccessModal('Success', `Bill ${inv} saved. ${journalLine}`, 'OK');
        this.$router.push(`/company/${this.$route.params.id}/dashboard/vendors`);
      }
    },
    validateRequiredFields() {
      const errors = [];
      
      // Check if vendor is selected
      if (!this.vendorId || this.vendorId === '') {
        errors.push('• Vendor Name is required');
      }
      
      // Check if bill date is selected
      if (!this.billDate || this.billDate === '') {
        errors.push('• Bill Date is required');
      }
      
      // Service Date automatically uses Bill Date, so no validation needed
      
      return errors;
    },
    showValidationError(errors) {
      this.errorModalTitle = 'Please Complete Required Fields';
      this.errorModalMessage = `Please fix the following issues before saving:\n\n${errors.join('\n')}`;
      this.errorModalButtonText = 'OK';
      this.showErrorModal = true;
    },
    /** Consultant Hours is the default itemize tab unless the route requests another. */
    applyDefaultItemizeTab() {
      const qTab = String(this.$route?.query?.activeTab || '').trim().toLowerCase();
      if (qTab === 'account' || qTab === 'product') {
        this.activeTab = qTab;
        return;
      }
      this.activeTab = 'consultant';
    },
  },
  watch: {
    async vendorId(val) {
      if (val === '__create_new__') {
        // Navigate to AddNewVendor.vue
        const companyId = this.$route.params.id;
        this.$router.push(`/company/${companyId}/dashboard/vendors/new-vendor`);
        // Optionally reset vendorId for UX
        this.vendorId = '';
        return;
      }
      if (!val) {
        this.activeTab = 'consultant';
      } else {
        this.applyDefaultItemizeTab();
      }
      const selected = this.vendorList.find(v => String(v.id) === String(val));
      if (selected) {
        // Update product/service items based on selected vendor
        await this.fetchProductServiceItems(val);
        this.contactList = selected.contacts || [];
        
        // Automatically select contact
        if (this.contactList.length > 0) {
          const primaryContact = this.contactList.find(contact => contact.primary);
          if (primaryContact) {
            this.contactName = primaryContact.name;
            this.phone = primaryContact.phone || '';
          } else {
            this.contactName = this.contactList[0].name;
            this.phone = this.contactList[0].phone || '';
          }
        } else {
          this.contactName = '';
          this.phone = '';
        }

        try {
          const bills = await ApiService.getVendorBills(val);
          this.vendorBills = Array.isArray(bills) ? bills : [];
        } catch (err) {
          logger.error('Error fetching vendor bills:', err);
          this.vendorBills = [];
        }

        if (selected.paymentTermId) {
          const matchingPaymentTerm = this.paymentTermsList.find(term =>
            String(term.id) === String(selected.paymentTermId)
          );
          if (matchingPaymentTerm) {
            this.paymentTerm = String(selected.paymentTermId);
            this.previousPaymentTerm = String(selected.paymentTermId);
            this.showSuccessModal('Info', `Auto-selected payment term: ${matchingPaymentTerm.name}`);
          } else {
            logger.warn(`Vendor's preferred payment term ID ${selected.paymentTermId} not found in payment terms list`);
          }
        }

        await this.fetchExpenseAccounts(val);
        await this.fetchConsultantsForVendor(val);
      } else {
        // No vendor selected - show all product/service items
        await this.fetchProductServiceItems(null);
        this.contactList = [];
        this.contactName = '';
        this.phone = '';
        this.vendorBills = [];
        // Reset payment term when no vendor is selected
        this.paymentTerm = '';
        this.previousPaymentTerm = '';
        // Reset consultant data
        // Always keep consultant section visible
        this.consultantTimesheets = [];
        this.consultantOptions = [];
        this.allConsultantOptions = [];
        this.consultantsError = '';
        // Clear account rows and mappings when no vendor is selected
        this.accountRows = [];
        this.expenseAccountMappings = [];
      }
      await this.generateInvNo();
    },
    contactName(val) {
      const selectedContact = this.contactList.find(c => c.name === val);
      if (selectedContact) {
        this.phone = selectedContact.phone || '';
      } else {
        this.phone = '';
      }
    },
    async paymentTerm() {
      this.updateDueDate();
      await this.generateInvNo();
    },
    async billDate() {
      // Automatically sync Service Date with Bill Date
      this.deliveryDate = this.billDate;
      await this.generateInvNo();
    },
    async serialNo() {
      await this.generateInvNo();
    },
    payFrom() {
      this.validatePayFromBalance();
    },
    accountRows: {
      handler() {
        this.validatePayFromBalance();
      },
      deep: true
    },
    productRows: {
      handler() {
        this.validatePayFromBalance();
        // Update account row amounts when product/service rows change
        this.updateAccountRowAmounts();
      },
      deep: true
    },
    consultantTimesheets: {
      handler() {
        this.validatePayFromBalance();
        // Update account row amounts when consultant timesheets change
        this.updateAccountRowAmounts();
      },
      deep: true
    },
  },
  beforeUnmount() {
    Object.values(this.attachmentPreviewUrls || {}).forEach(url => {
      if (url) URL.revokeObjectURL(url);
    });
    this.attachmentPreviewUrls = {};
  },
};
</script>

<style scoped>
.vendor_enter_bill-breakdown-row {
  background-color: #f8f9fa;
  border-left: 3px solid #e9ecef;
}

.vendor_enter_bill-breakdown-row td {
  border-top: 1px solid #e9ecef;
  border-bottom: 1px solid #e9ecef;
}

.vendor_enter_bill-breakdown-row:hover {
  background-color: #f1f3f4;
}

.vendor_enter_bill-source-row {
  background-color: #f0f8ff;
  border-left: 3px solid #b3d9ff;
}

.vendor_enter_bill-source-row td {
  border-top: 1px solid #e6f3ff;
  border-bottom: 1px solid #e6f3ff;
}

.vendor_enter_bill-source-row:hover {
  background-color: #e6f3ff;
}

.vendor_enter_bill-source-indicator {
  font-size: 0.85em;
  color: #1976d2;
  font-weight: 500;
}

/* Bill Status Styling */
.vendor_enter_bill-status-available {
  color: #28a745;
  font-weight: 500;
  background-color: #d4edda;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.8em;
}

.vendor_enter_bill-status-bill-created {
  color: #ffc107;
  font-weight: 500;
  background-color: #fff3cd;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.8em;
}

.vendor_enter_bill-status-paid {
  color: #17a2b8;
  font-weight: 500;
  background-color: #d1ecf1;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.8em;
}

.vendor_enter_bill-status-not-applicable {
  color: #6c757d;
  font-weight: 500;
  background-color: #f8f9fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.8em;
}

.vendor_enter_bill-status-default {
  color: #6c757d;
  font-weight: 500;
  background-color: #f8f9fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.8em;
}

/* Individual Account Item Styling */
.vendor_enter_bill-item-description {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.vendor_enter_bill-item-name {
  font-weight: 500;
  color: #333;
  font-size: 0.95em;
}

.vendor_enter_bill-item-details {
  font-size: 0.85em;
  color: #666;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.vendor_enter_bill-source-info {
  font-size: 0.8em;
  color: #999;
  font-style: italic;
}
</style> 